import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import GlobalLayout from './GlobalLayout';
import './ElementFeed.css';
import { getCollectionByHash, getElementByHash, getElementById, setChallenge } from '../services/apiService';
import { useLanguage } from '../context/LanguageContext';
import { useExternalBackButton } from '../hooks/useExternalBackButton';
import { useAuth } from '../context/AuthContext';
import { appConfig } from '../config';
import QRScanner from './QRScanner';
import GlobalChatWidget from './GlobalChatWidget';

// --- NEU: Helper Funktion für Challenge Typ 4 (Text-Optionen) ---
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  
  // 1. Priorität: Gewählte Sprache (z.B. option1_de)
  const localizedField = `${field}_${code}`;
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }

  // --- FIX: Sonderbehandlung für Deutsch ---
  // Wenn Deutsch gewünscht ist, aber 'name_de' leer ist, nehmen wir das Hauptfeld 'name'.
  // Wir dürfen NICHT zum englischen Fallback springen.
  if (code === 'de') {
      return item[field] || '';
  }

  // 2. Priorität: Englisch als Fallback für andere Sprachen (z.B. option1_en)
  // Nur nutzen, wenn wir NICHT gerade Deutsch suchen (das wurde oben schon abgefangen)
  const enField = `${field}_en`;
  if (item[enField] && String(item[enField]).trim() !== '') {
    return item[enField];
  }

  // 3. Priorität: Default Feld (z.B. option1) als letzter Ausweg
  return item[field] || '';
};


// HELPER: Konvertiert SRT zu VTT
// HELPER: Parst SRT-Text in Cue-Objekte {start, end, text} (Sekunden als Float).
// Wird für das Custom-Subtitle-Overlay verwendet — KEINE nativen <track>-Elemente mehr,
// damit Safari keine dunkle Box rendert und wir volle CSS-Kontrolle haben.
const parseSrtToCues = (srtText) => {
  if (!srtText) return [];

  const timeToSec = (str) => {
    // Unterstützt "HH:MM:SS,mmm" (SRT) und "HH:MM:SS.mmm" (VTT)
    const clean = str.trim().replace(',', '.');
    const [hms, msStr] = clean.split('.');
    const ms = parseInt(msStr || '0', 10) / 1000;
    const [h, m, s] = (hms || '0:0:0').split(':').map(Number);
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0) + ms;
  };

  const clean = srtText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const blocks = clean.split(/\n\s*\n/).filter(b => b.trim());
  const cues = [];

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    let si = 0;
    if (/^\d+$/.test(lines[0]?.trim())) si = 1;

    const tl = lines[si];
    if (!tl || !tl.includes('-->')) continue;

    const match = tl.match(
      /(\d{2}:\d{2}:\d{2}[.,]\d{3})\s+-->\s+(\d{2}:\d{2}:\d{2}[.,]\d{3})/
    );
    if (!match) continue;

    const text = lines.slice(si + 1).join('\n').trim();
    if (!text) continue;

    cues.push({ start: timeToSec(match[1]), end: timeToSec(match[2]), text });
  }

  return cues;
};

const ElementFeed = () => {
  const { hashId } = useParams(); // collection hash
  const location = useLocation();
  const navigate = useNavigate(); // <--- NEU
  const { t, currentLanguage } = useLanguage();

  // --- GAMIFICATION STATE ---
  const [achievementQueue, setAchievementQueue] = useState([]); // Warteschlange
  const [currentAchievement, setCurrentAchievement] = useState(null); // Das aktuell angezeigte

  
  
  const { setProgress: setProgressAPI } = useAuth();

  // ── Quiz-XP-Helper ────────────────────────────────────────────────────────
  // Wird nach jeder korrekten Quiz-Antwort aufgerufen.
  // Setzt progress=100 → ProgressController Weg B (max_progress >= 90%) → XP-Vergabe.
  // Achievement-Queue wird wie beim Video-Tracking befüllt.
  const awardQuizXP = useCallback(async (lesson) => {
    const hash = lesson?.hash_id ?? lesson?.hashId;
    if (!hash) return;
    try {
      const result = await setProgressAPI(hash, 100);
      if (result?.data?.new_achievements?.length > 0) {
        setAchievementQueue(prev => [...prev, ...result.data.new_achievements]);
      }
    } catch (_) {}
  }, [setProgressAPI]);
  
  
  const [lessons, setLessons] = useState(() => {
    const fromState = location?.state?.lessons;
    return Array.isArray(fromState) ? fromState : [];
  });

  const startIndex = useMemo(() => {
    const idx = Number(location?.state?.startIndex ?? 0);
    return Number.isFinite(idx) ? Math.max(0, idx) : 0;
  }, [location]);

  const collectionData = useMemo(() => location?.state?.collectionData || null, [location?.state?.collectionData]);
  const isDebug = useMemo(() => {
    try {
      const sp = new URLSearchParams(location?.search || window.location.search || '');
      return sp.get('debug') === '1';
    } catch (_) {
      return false;
    }
  }, [location]);
  const isAutoplayEnabled = useMemo(() => {
    try {
      const sp = new URLSearchParams(location?.search || window.location.search || '');
      const qp = sp.get('autoplay');
      if (qp != null) return qp === '1' || qp === 'true';
    } catch (_) {}
    // default from config, fallback true
    return appConfig?.videoAutoplay !== false;
  }, [location]);
  const chapterStartTime = useMemo(() => location?.state?.chapterStartTime || null, [location]);
  const hasAppliedChapterSeekRef = useRef(false);
  const hasRotatedForElementDetailRef = useRef(false);
  const lastVideoHashRef = useRef(null);

  const containerRef = useRef(null);
  // useScrollSnap entfernt – Konflikt mit CSS scroll-snap + verursacht Bounce-Loop beim Hochswipen

  
  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [isSimFullscreen, setIsSimFullscreen] = useState(true);
  const [isMuted, setIsMuted] = useState(() => {
    const savedMuteState = localStorage.getItem('hejvi_video_muted');
    return savedMuteState !== null ? savedMuteState === 'true' : true;
  });

  const [subtitleTracks, setSubtitleTracks] = useState({});



// SUBTITLES STATE (Standard: Immer AN)
  const [showSubtitles, setShowSubtitles] = useState(true);

  // WICHTIG: Untertitel immer aktivieren, wenn das Video wechselt (Swipe oder Autoplay)
  useEffect(() => {
    setShowSubtitles(true);
  }, [activeIndex]);
  
  
  const [currentTime, setCurrentTime] = useState(0);

  const [duration, setDuration] = useState(0);
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const [showEndPopup, setShowEndPopup] = useState(false);
  const [endPopupTimer, setEndPopupTimer] = useState(3);
  
  
  // Test function to verify API is working
  const testProgressAPI = async () => {
    const currentLesson = lessons[activeIndex];
    if (currentLesson?.hash_id) {
      try {
        console.log('🧪 Testing progress API with 50%...');
        const result = await setProgressAPI(currentLesson.hash_id, 50);
        console.log('🧪 Test API response:', result);
        alert('API Test: Progress set to 50% - Check console for details');
      } catch (error) {
        console.error('🧪 Test API error:', error);
        alert('API Test Failed - Check console for error');
      }
    } else {
      alert('No active lesson to test with');
    }
  };
  const [showSkipIndicator, setShowSkipIndicator] = useState(null);
  const [challengeState, setChallengeState] = useState({});
  const [hintHighlights, setHintHighlights] = useState({});
  const hintTimersRef = useRef({});
  const [responseVideos, setResponseVideos] = useState({});
  const [returnToChallenge, setReturnToChallenge] = useState(null);
  const [overlayProgress, setOverlayProgress] = useState({});
  const timerOverridesRef = useRef({});
  const overlayVideoRef = useRef(null);
  const overlayEndedOnceRef = useRef({});
  const overlaySourceRef = useRef({});
  
  // Platform detection for iOS compatibility
  const isIOS = useMemo(() => {
    try {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    } catch (_) {
      return false;
    }
  }, []);

  // Function to normalize challenge type for consistent behavior across platforms
  const getNormalizedChallengeType = useCallback((lesson) => {
    if (!lesson) return 0;
    
    // If lesson has explicit challenge_type, use it
    if (lesson.challenge_type !== undefined && lesson.challenge_type !== null) {
      return Number(lesson.challenge_type);
    }
    
    // For iOS compatibility: if it's a type 3 lesson (challenge) but no challenge_type is set,
    // Fallback Logik für iOS / fehlende Daten
    if (Number(lesson.type) === 3) {
      const correctOption = String(lesson?.correct_option || lesson?.correct_answer || lesson?.correct || '').toLowerCase();
      
      // A) QR Code Check
      if (correctOption.includes('qr') || correctOption.includes('hejvi-qr')) {
        return 2;
      }
      
      // B) Multi Option Check (Priorität vor Yes/No!)
      // Wenn number_of_options gesetzt ist und > 0, ist es wahrscheinlich Type 3
      if (lesson.number_of_options && Number(lesson.number_of_options) >= 2) {
        return 3;
      }
      
      // C) Yes/No Check
      // KORREKTUR: '0' und '1' entfernt, da diese auch Indizes sein können. 
      // Nur eindeutige Textwerte triggern den Zwang zu Type 0.
      if (['yes', 'no', 'true', 'false'].includes(correctOption)) {
        return 0;
      }
      
      // Fallback: Wenn wir number_of_options haben, ist es 3, sonst 0
      if (lesson.option1_name || lesson.option1_thumb) return 3;
      
      return 0;
    }
    
    // Default to 0 (yes/no) for other cases
    return 0;
  }, []);
  
  // QR scanner modal state and target index
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const qrPendingIndexRef = useRef(null);

  // Unknown QR code modal state
  const [unknownQRModal, setUnknownQRModal] = useState({ isOpen: false, scannedCode: null });

  // ── Rendering-Status: Video noch nicht fertig (FFmpeg läuft im Hintergrund) ──
  // renderingElements[idx] = true → stream_0.m3u8 noch nicht da → Overlay zeigen
  const [renderingElements, setRenderingElements] = useState({});
  const renderingPollRef = useRef({}); // idx → intervalId

// --------------------------------------------------------
  // NEU: Audio-Spur basierend auf App-Sprache wählen
  // --------------------------------------------------------
  // --------------------------------------------------------
  // NEU: Audio-Spur wählen (Gibt den gewählten Index zurück)
  // --------------------------------------------------------
  const applyAudioTrackPreference = useCallback((videoElement, hlsInstance, eventData = null) => {
    if (!videoElement) return -1;

    // Sprache ermitteln
    const targetLang = currentLanguage 
      ? String(currentLanguage).toLowerCase().trim().split('-')[0] 
      : 'de';
    const fallbackLang = 'de';

    const isMatch = (trackLang, searchLang) => {
      if (!trackLang) return false;
      return String(trackLang).toLowerCase().startsWith(searchLang);
    };

    // --- A) HLS.js (Chrome / Firefox) ---
    if (hlsInstance) {
      // WICHTIG: Nimm Tracks aus Event-Daten (sind frischer) oder von der Instanz
      const tracks = eventData?.audioTracks || hlsInstance.audioTracks || [];
      
      console.log(`[HEJVI AUDIO] Checking ${tracks.length} tracks for target "${targetLang}"`);

      if (tracks.length > 0) {
          let bestIdx = tracks.findIndex(tr => isMatch(tr.lang || tr.language || tr.name, targetLang));
          
          if (bestIdx === -1) {
            console.warn(`Target "${targetLang}" not found. Trying fallback "${fallbackLang}"...`);
            bestIdx = tracks.findIndex(tr => isMatch(tr.lang || tr.language || tr.name, fallbackLang));
          }
          if (bestIdx === -1) bestIdx = 0; 

          // WICHTIG: Wir setzen den Track direkt hier
          if (bestIdx !== -1) {
             hlsInstance.audioTrack = bestIdx;
             console.log(`✅ HLS: Set Audio Track to Index ${bestIdx} (${tracks[bestIdx].lang})`);
             return bestIdx;
          }
      }
      return -1;
    } 
    
    // --- B) Native HLS (Safari) ---
    if (videoElement.audioTracks && videoElement.audioTracks.length > 0) {
      const tracks = videoElement.audioTracks;
      let chosenTrack = null;
      for (let i = 0; i < tracks.length; i++) {
        if (isMatch(tracks[i].language, targetLang)) { chosenTrack = tracks[i]; break; }
      }
      if (!chosenTrack) {
         for (let i = 0; i < tracks.length; i++) {
             if (isMatch(tracks[i].language, fallbackLang)) { chosenTrack = tracks[i]; break; }
         }
      }
      if (!chosenTrack) chosenTrack = tracks[0];

      if (chosenTrack) {
         for (let i = 0; i < tracks.length; i++) {
            tracks[i].enabled = (tracks[i] === chosenTrack);
         }
         console.log(`✅ Safari: Switched to "${chosenTrack.language}"`);
      }
      return 1;
    }
    return -1;
  }, [currentLanguage]);

  // Effekt: Wenn Sprache geändert wird -> Auf alle Videos anwenden
  // HINWEIS: Stelle sicher, dass 'videoRefs' und 'hlsInstancesRef' in deiner Datei so heißen.
  // Falls sie anders heißen (z.B. videosRef), passe den Namen hier an.
  useEffect(() => {
    if (videoRefs.current) {
      // Wenn videoRefs ein Array ist (wie im Standard-Code)
      if (Array.isArray(videoRefs.current)) {
         videoRefs.current.forEach((vid, idx) => {
            const hls = hlsInstancesRef.current ? hlsInstancesRef.current[idx] : null;
            if (vid) applyAudioTrackPreference(vid, hls);
         });
      } 
      // Falls videoRefs ein Objekt ist (Dictionary-Style), iteriere über Values
      else {
         Object.values(videoRefs.current).forEach(vid => {
             // Hier müssten wir die passende HLS Instanz finden. 
             // Im Zweifel nur native Tracks prüfen:
             if(vid) applyAudioTrackPreference(vid, null);
         });
      }
    }
  }, [currentLanguage, applyAudioTrackPreference]);

  // --- GAMIFICATION: Queue Abarbeitung ---
  useEffect(() => {
    // Wenn nichts angezeigt wird, aber was in der Warteschlange ist -> Zeige das nächste
    if (!currentAchievement && achievementQueue.length > 0) {
      const next = achievementQueue[0];
      setCurrentAchievement(next);
      setAchievementQueue(prev => prev.slice(1)); // Erstes Element entfernen
    }
  }, [currentAchievement, achievementQueue]);
  
  // Set up external back button handling
  // 1. Umbenennen in defaultBackClick
  const { handleBackClick: defaultBackClick } = useExternalBackButton('/dashboard');

  // 2. Custom Handler für Search Context & Course Navigation
  const handleCustomBack = useCallback(() => {
    const state = location.state || {};
    const { sourcePage, collectionData, searchContext, openChatOnReturn } = state;

    // A) Chat-Restore-Kette: Kommen wir aus dem Chat-Modal?
    // navigate(-1) geht zurück zur ElementPage, die noch openChatOnReturn=true
    // in ihrem History-Entry hat → ElementPage leitet dann mit restoreChat weiter.
    if (openChatOnReturn) {
      navigate(-1);
      return;
    }

    // --- FIX: Zurück zur Kurs-Ansicht (Collection) ---
    // Wenn wir aus einem Kurs kamen UND Collection-Daten haben,
    // geben wir diese zurück, damit CourseDetail nicht neu laden muss.
    if (sourcePage && sourcePage.includes('/course/') && collectionData) {
        console.log('[ElementFeed] Custom back to Course with data:', sourcePage);
        navigate(sourcePage, {
            state: {
                collectionData: collectionData, // Das ist wichtig für die Ansicht dort
                searchContext: searchContext    // Filter beibehalten, falls vorhanden
            }
        });
        return;
    }
    // ------------------------------------------------

    // B) Zurück mit SearchContext (z.B. Dashboard)
    if (searchContext && sourcePage) {
      console.log('[ElementFeed] Custom back with search context to:', sourcePage);
      navigate(sourcePage, { 
        state: { 
          searchContext: searchContext 
        } 
      });
      return; 
    } 
    
    // C) Fallback (Standardverhalten)
    defaultBackClick();
    
  }, [location.state, navigate, defaultBackClick]);
  
  // State:
  const [globalChatOpen, setGlobalChatOpen] = useState(false);
  
  const lastProgressSecondRef = useRef(-1);
  const lastProgressSentAtRef = useRef(0);
  const prevActiveIndexRef = useRef(-1);
  const progressIntervalRef = useRef(null);

  // Progress tracking using setProgress API
  const sendPlaybackProgress = useCallback(async (payload) => {
    try {
      if (!appConfig?.workflow?.enableProgressTracking) {
        console.log('⚠️ Progress tracking disabled in config');
        return;
      }
      
      const { hash_id, current, duration, event } = payload;
      if (!hash_id || !duration || duration <= 0) {
        console.log('⚠️ Invalid progress data:', { hash_id, current, duration, event });
        return;
      }

      // Calculate progress percentage
      const progressPercentage = Math.min(100, Math.max(0, (current / duration) * 100));
      
      // Only send progress updates for 'progress' events, not play/pause/ended
      if (event === 'progress') {
        try {
          //console.log(`📤 Sending progress to API: ${hash_id} -> ${progressPercentage.toFixed(1)}% (${current}s / ${duration}s)`);
          const result = await setProgressAPI(hash_id, progressPercentage);
          //console.log(`✅ Progress API response:`, result);
          // --- NEU: GAMIFICATION CHECK ---
          // Prüfen, ob "new_achievements" im Response data objekt steckt
           if (result && result.data && result.data.new_achievements && Array.isArray(result.data.new_achievements)) {
              if (result.data.new_achievements.length > 0) {
                  // In die Queue schieben
                  setAchievementQueue(prev => [...prev, ...result.data.new_achievements]);
              }
          }
         
        } catch (error) {
          console.error('❌ Failed to update progress:', error);
        }
      } else {
        console.log(`📊 Progress event: ${event} - ${hash_id} (${current}s / ${duration}s)`);
      }
    } catch (error) {
      console.error('Progress tracking error:', error);
    }
  }, [setProgressAPI, appConfig]);

  const startProgressInterval = useCallback((vid, lesson, index) => {
    try { if (progressIntervalRef.current) clearInterval(progressIntervalRef.current); } catch (_) {}
    console.log(`🎬 Starting progress tracking for: ${lesson?.name || 'Unknown'} (${lesson?.hash_id})`);
    progressIntervalRef.current = setInterval(() => {
      try {
        if (!vid || vid.paused || vid.ended || !Number.isFinite(vid.duration)) return;
        const now = Date.now();
        const currentTime = Math.max(0, Math.floor(vid.currentTime || 0));
        const duration = Math.max(0, Math.floor(vid.duration || 0));
        
        // Log every 5 seconds to avoid spam
        /*if (currentTime % 5 === 0) {
          console.log(`⏱️ Progress tracking: ${currentTime}s / ${duration}s = ${((currentTime / duration) * 100).toFixed(1)}%`);
        }*/
        
        sendPlaybackProgress({
          event: 'progress',
          id: lesson?.id ?? null,
          hash_id: lesson?.hash_id ?? null,
          name: lesson?.name ?? lesson?.title ?? null,
          index,
          current: currentTime,
          duration: duration,
          paused: !!vid.paused,
          ts: now
        });
      } catch (_) {}
    }, 1000);
  }, [sendPlaybackProgress]);

  const stopProgressInterval = useCallback(() => {
    try { 
      if (progressIntervalRef.current) {
        console.log('⏹️ Stopping progress tracking');
        clearInterval(progressIntervalRef.current); 
      }
    } catch (_) {}
    progressIntervalRef.current = null;
  }, []);

  const [scrubState, setScrubState] = useState({ active: false, idx: -1, pct: 0 });
  const scrubRectRef = useRef(null);
  const [hoverState, setHoverState] = useState({ active: false, idx: -1, pct: 0 });
  const applyMuteState = useCallback((muted) => {
    try {
      const mainVid = videoRefs.current[activeIndex];
      if (mainVid) {
        mainVid.muted = muted;
        if (!muted) mainVid.play().catch(() => {});
      }
    } catch (_) {}
    try {
      const overlay = overlayVideoRef.current;
      if (overlay && overlay instanceof HTMLVideoElement) {
        overlay.muted = muted;
        if (!muted) overlay.play().catch(() => {});
      }
    } catch (_) {}
  }, [activeIndex]);



  // --------------------------------------------------------
  // FIX: Untertitel-Spur aktiv umschalten (React 'default' reicht oft nicht)
  // --------------------------------------------------------
  
 
  // --------------------------------------------------------
  // UNTERTITEL VERARBEITEN (SRT Text -> VTT Blob)
  // MIT ARRAY-SUPPORT FIX
  // --------------------------------------------------------
  useEffect(() => {
    if (!lessons || lessons.length === 0) return;

    lessons.forEach((lesson, idx) => {
      // 1. Prüfen ob schon geladen
      if (subtitleTracks[idx]) return;

      // 2. Prüfen ob Daten da sind
      if (!lesson.subtitles) {
          // console.log(`[HEJVI SUBS] Lesson ${idx}: No subtitles.`);
          return;
      }

      try {
        let subs = lesson.subtitles;
        
        // 3. JSON Parsing (falls es ein String ist)
        if (typeof subs === 'string') {
            if (subs.trim().startsWith('{') || subs.trim().startsWith('[')) {
                try { 
                    subs = JSON.parse(subs); 
                } catch (e) {
                    console.warn(`[HEJVI SUBS] JSON Parse Error:`, e);
                    return; 
                }
            } else {
                 // Fallback: Kein JSON, wir nehmen an es ist reiner SRT Text auf Deutsch
                 subs = { "DE": subs };
            }
        }

        const tracks = [];

        // 4. FALL A: Das neue Array-Format aus der Datenbank
        // Format: [ ["DE", 1, "00:00..", "Text", 2, "00:00..", "Text"], ["EN", ...] ]
        if (Array.isArray(subs)) {
            console.log(`[HEJVI SUBS] Lesson ${idx}: Detected Array format. Reconstructing SRT...`);
            
            subs.forEach(row => {
                if (!Array.isArray(row) || row.length < 2) return;

                const langKey = row[0]; // Erstes Element ist Sprache (z.B. "DE")
                let srtBuilder = "";

                // Wir iterieren ab Index 1 in 3er Schritten (Index, Zeit, Text)
                // Beispiel: 1, "00:00 --> 00:02", "Hallo Welt"
                for (let i = 1; i < row.length; i += 3) {
                    const num = row[i];
                    const time = row[i+1];
                    const text = row[i+2];

                    if (num !== undefined && time && text) {
                        srtBuilder += `${num}\n${time}\n${text}\n\n`;
                    }
                }

                if (srtBuilder.length > 0) {
                    const cues = parseSrtToCues(srtBuilder);
                    if (cues.length > 0) {
                        tracks.push({
                            lang: String(langKey).toLowerCase(),
                            label: String(langKey).toUpperCase(),
                            cues
                        });
                    }
                }
            });
        } 
        // 5. FALL B: Das alte Objekt-Format
        // Format: { "DE": "1\n00:00..", "EN": "..." }
        else if (typeof subs === 'object' && subs !== null) {
            console.log(`[HEJVI SUBS] Lesson ${idx}: Detected Object format.`);
            Object.entries(subs).forEach(([langKey, srtContent]) => {
                if (!srtContent || typeof srtContent !== 'string') return;
                const cues = parseSrtToCues(srtContent);
                if (cues.length > 0) {
                    tracks.push({
                        lang: langKey.toLowerCase(),
                        label: langKey.toUpperCase(),
                        cues
                    });
                }
            });
        }

        console.log(`[HEJVI SUBS] Lesson ${idx}: Successfully created ${tracks.length} tracks.`);

        if (tracks.length > 0) {
            setSubtitleTracks(prev => ({ ...prev, [idx]: tracks }));
        }

      } catch (err) {
        console.error("[HEJVI SUBS] Critical Error:", idx, err);
      }
    });
  }, [lessons]);

  // Alle nativen Text-Tracks vom Video-Element verstecken.
  // Untertitel werden jetzt als Custom-Overlay gerendert (ef-subtitle-overlay),
  // damit kein Browser native Box-Rendering (insb. Safari) auftritt.
  useEffect(() => {
    const hideAllTracks = (video) => {
        if (!video?.textTracks) return;
        Array.from(video.textTracks).forEach(t => { t.mode = 'hidden'; });
    };
    videoRefs.current.forEach(hideAllTracks);

    // addtrack: auch nachträglich injizierte HLS-Tracks sofort verstecken
    const cleanups = videoRefs.current.map((video) => {
        if (!video?.textTracks) return null;
        const handler = () => hideAllTracks(video);
        video.textTracks.addEventListener('addtrack', handler);
        return () => video.textTracks.removeEventListener('addtrack', handler);
    });
    return () => { cleanups.forEach(fn => fn && fn()); };
  }, [subtitleTracks, activeIndex]);
  /**
   * Utility function to analyze timer settings for all video elements in a collection
   * Each video element should have a property `timer_on_end` coming from API
   * 
   * Based on  JSON structure:
   * - timer_on_end: 0 = No timer, immediately go to next video
   * - timer_on_end: 1 = Show 3-second countdown timer popup
   * 
   * Examples from  JSON:
   * - "Angle grinder": timer_on_end: 0 (no timer)
   * - "Tube press": timer_on_end: 1 (show timer)
   * - "Sanding challenge": timer_on_end: 1 (show timer)
   * 
   * NOTE: This function is now integrated into the main video event handler
   * for better performance and to avoid conflicts.
   */
  const analyzeCollectionTimers = useCallback((videoElements) => {
    if (!Array.isArray(videoElements)) {
      console.log('[HEJVI DEBUG] analyzeCollectionTimers: Invalid videoElements array');
      return { summary: 'Invalid input', timers: [] };
    }

    console.log('[HEJVI DEBUG] Analyzing timer settings for', videoElements.length, 'elements');

    const timerAnalysis = videoElements.map((videoElement, idx) => {
      const timerOnEnd = Number(videoElement?.timer_on_end ?? 1);
      setShowSubtitles(true);
      const analysis = {
        index: idx,
        name: videoElement?.name || `Element ${idx}`,
        id: videoElement?.id,
        timer_on_end: timerOnEnd,
        behavior: timerOnEnd === 1 ? 'Show 3s countdown timer' : 'Immediately go to next video'
      };
      
      console.log(`[HEJVI DEBUG] Element ${idx} (${videoElement?.name}): timer_on_end=${timerOnEnd} → ${analysis.behavior}`);
      return analysis;
    });

    const summary = {
      total: videoElements.length,
      withTimer: timerAnalysis.filter(t => t.timer_on_end === 1).length,
      withoutTimer: timerAnalysis.filter(t => t.timer_on_end === 0).length
    };

    console.log('[HEJVI DEBUG] Timer analysis complete:', summary);
    return { summary, timers: timerAnalysis };
  }, []);

  // Example usage function for manual timer analysis (can be called from console or other components)
  const manualAnalyzeTimers = useCallback((videoElements) => {
    console.log('[HEJVI DEBUG] Manual timer analysis requested');
    return analyzeCollectionTimers(videoElements || lessons);
  }, [analyzeCollectionTimers, lessons]);

  // Helpers to resolve success/incorrect element IDs to media URLs
  const resolveElementMediaById = useCallback((elementId) => {
    if (!elementId) return { url: '', thumbnail: '' };
    const idStr = String(elementId);
    const element = (lessons || []).find((el) => {
      if (idStr.startsWith('el-')) {
        return String(el?.hash_id || el?.hashId) === idStr;
      }
      return Number(el?.id) === Number(elementId);
    });
    return {
      url: normalizeMediaUrl(element?.url_element || ''),
      thumbnail: normalizeMediaUrl(element?.url_thumbnail || '')
    };
  }, [lessons]);

  // Fetch response video by hash from API
  const fetchResponseVideo = useCallback(async (hash) => {
    if (!hash) {
      console.log('[HEJVI DEBUG] fetchResponseVideo: No hash provided');
      return { url: '', thumbnail: '' };
    }

    console.log('[HEJVI DEBUG] fetchResponseVideo called with hash:', hash);

    if (responseVideos[hash] && !responseVideos[hash].error) {
      console.log('[HEJVI DEBUG] fetchResponseVideo: Using cached result:', responseVideos[hash]);
      return responseVideos[hash];
    }
    
    if (responseVideos[hash] && responseVideos[hash].error) {
      console.log('[HEJVI DEBUG] fetchResponseVideo: Cached result has error, will re-fetch once:', responseVideos[hash]);
    }

    setResponseVideos(prev => ({ ...prev, [hash]: { loading: true, url: '', thumbnail: '', error: '' } }));

    try {
      const res = await getElementByHash(hash);
      const element = res?.data || res || {};
      const rawUrl = element?.url_element || element?.url || '';
      const result = {
        url: normalizeMediaUrl(rawUrl),
        thumbnail: normalizeMediaUrl(element?.url_thumbnail || element?.thumbnail || ''),
        loading: false,
        error: ''
      };
      setResponseVideos(prev => ({ ...prev, [hash]: result }));
      return result;
    } catch (error) {
      const result = { url: '', thumbnail: '', loading: false, error: 'Response video not found for provided hash' };
      setResponseVideos(prev => ({ ...prev, [hash]: result }));
      return result;
    }
  }, [responseVideos, lessons]);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const lastTapTime = useRef(0);
  const lastTapX = useRef(0);
  const lastTapY = useRef(0);
  const singleTapTimeout = useRef(null);
  const videoRefs = useRef([]);
  const [videoErrors, setVideoErrors] = useState({});
  const hlsInstancesRef = useRef({});
  const navigatingRef = useRef(false);

  const normalizeMediaUrl = (url) => {
    if (!url) return '';
    let cleanPath = url;
    
    // 1. Base URL Logik
    if (!cleanPath.startsWith('http')) {
       cleanPath = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
       const baseUrl = appConfig.baseUrl.endsWith('/') ? appConfig.baseUrl : `${appConfig.baseUrl}/`;
       cleanPath = `${baseUrl}${cleanPath}`;
    }

    // KORREKTUR: Wir entfernen hier den automatischen .mp4 -> .m3u8 Replace!
    // Grund: Das bricht den Fallback. Wir machen das dynamisch im Player.
    
    return cleanPath;
  };

  // ── Secure Media URL ─────────────────────────────────────────────────────────
  // Hängt den JWT als ?token= an alle /media/clients/-Pfade an.
  // Warum: Cookie-Auth ist zuverlässig für same-origin-Requests, versagt aber bei:
  //   - abgelaufenem Cookie (TTL kürzer als Token-Laufzeit)
  //   - <video crossOrigin="anonymous"> in Kombination mit CORS-Preflights
  //   - Safari ITP in bestimmten Privacy-Modes
  //   - Timing-Lücke zwischen App-Start und checkExistingAuth
  // ?token= wird von media-proxy.php als Priorität 2 (nach Authorization-Header) gelesen.
  // HLS.js hat bereits xhrSetup mit Authorization-Header — hier nur für browser-native
  // Requests (poster, backgroundImage, <img>, Safari native HLS, MP4-Fallback).
  const getSecureMediaUrl = useCallback((url) => {
    const normalized = normalizeMediaUrl(url);
    if (!normalized || !normalized.includes('/media/clients/')) return normalized;
    const token = localStorage.getItem('hejvi_access_token') || '';
    if (!token) return normalized; // kein Token → URL unverändert (auth schlägt sowieso fehl)
    const sep = normalized.includes('?') ? '&' : '?';
    return `${normalized}${sep}token=${encodeURIComponent(token)}`;
  }, []); // token wird fresh aus localStorage gelesen — kein State-Dep nötig
  // ─────────────────────────────────────────────────────────────────────────────

  const buildMediaUrl = (path) => {
    const safePath = String(path || '').startsWith('/') ? path : `/${path || ''}`;
    const base = String(appConfig.apiBaseUrl || '').replace(/\/$/, '');
    let url = `${base}${safePath}`;
    // Ensure absolute for media playback (avoids dev proxy edge-cases)
    if (!/^https?:\/\//i.test(url)) {
      url = `https://app.hejvi.de${url}`;
    }
    return normalizeMediaUrl(url);
  };

  // Fetch element video URL given an element ID or hash-like ID
  const fetchElementVideo = useCallback(async (elementId) => {
    try {
      // Prefer existing lesson mapping or hash-based lookup; avoid numeric ID API calls
      const { url } = resolveElementMediaById(elementId);
      if (url) return url;

      const idStr = String(elementId || '');
      if (idStr.startsWith('el-')) {
        const res = await getElementByHash(idStr);
        const apiUrl = res?.data?.data?.url_element || res?.data?.url_element || '';
        return normalizeMediaUrl(apiUrl);
      }
      // Try to resolve from lessons by numeric id
      const target = (lessons || []).find(l => Number(l?.id) === Number(elementId));
      if (target?.url_element) return normalizeMediaUrl(target.url_element);
      return '';
    } catch (_) {
      return '';
    }
  }, [resolveElementMediaById, lessons]);

  // playChallengeVideo is defined later, after its dependencies

  const formatTime = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  const backCourseUrl = useMemo(() => {
    const courseHash = collectionData?.hash_id || hashId;
    console.log('ElementFeed backCourseUrl - courseHash:', courseHash, 'sourcePage:', location?.state?.sourcePage, 'location.state:', location?.state);

    const isDirectAccess = !location?.state || Object.keys(location.state).length === 0;
    if (isDirectAccess) {
      console.log('ElementFeed detected direct access - going to dashboard');
      return '/dashboard';
    }

    if (courseHash && courseHash.startsWith('col-')) {
      console.log('ElementFeed going back to collection:', `/collection/${courseHash}`);
      return `/collection/${courseHash}`;
    }

    const sourcePage = location?.state?.sourcePage;
    if (sourcePage && sourcePage !== '/login' && sourcePage !== '/') {
      console.log('ElementFeed using sourcePage:', sourcePage);
      return sourcePage;
    }

    console.log('ElementFeed defaulting to dashboard');
    return '/dashboard';
  }, [collectionData, hashId, location?.state?.sourcePage]);

  useEffect(() => {
    const updateViewportHeight = () => {
      const height = window.innerHeight;
      setViewportHeight(`${height}px`);
      document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    const node = videoRefs.current[startIndex]?.closest('[data-snap-section]');
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    setActiveIndex(startIndex);
  }, [startIndex]);

  useEffect(() => {
    if (!Array.isArray(lessons) || lessons.length === 0) return;
    const idx = Number(location?.state?.startIndex ?? startIndex ?? 0);
    if (!Number.isFinite(idx) || idx < 0 || idx >= lessons.length) return;

    setActiveIndex(idx);
    hasRotatedForElementDetailRef.current = true;
    setTimeout(() => {
      const node = videoRefs.current[idx]?.closest('[data-snap-section]');
      if (node && node.scrollIntoView) {
        try { node.scrollIntoView({ behavior: 'instant', block: 'start' }); } catch (_) {}
      }
    }, 0);
  }, [lessons, startIndex, location?.state?.startIndex]);

  // --------------------------------------------------------
  // FIX: ROBUSTES DATEN-LADEN FÜR DIREKTE LINKS
  // --------------------------------------------------------
  useEffect(() => {
    const id = hashId;
    
    // Abbruchbedingungen: ID fehlt oder wir haben schon Daten (außer force Parameter ist da)
    if (!id) return;
    // GUARD: Playlist aus Navigation-State hat absoluten Vorrang —
    // ABER nur wenn alle Lessons bereits 'source_document' haben.
    // null = API hat geantwortet, kein Dokument → kein Re-fetch nötig.
    // undefined / Key fehlt = nie via API geladen → muss nachgeladen werden.
    const lessonsFromNav = Array.isArray(location?.state?.lessons) && location.state.lessons.length > 0;
    const navLessonsHaveSourceDoc = lessonsFromNav &&
      location.state.lessons.every(l => 'source_document' in l);
    if (navLessonsHaveSourceDoc && !location?.search?.includes('force=true')) return;

    // Nur bei echtem Direktzugriff: Re-fetch wenn keine oder veraltete Daten
    const hasStaleData = (lessons || []).some(l => !('source_document' in l));
    if ((lessons || []).length > 0 && !hasStaleData && !location?.search?.includes('force=true')) return;

    const normalizeLesson = (rawData) => {
      if (!rawData) return null;
      // Falls die Daten in einem Wrapper 'data' stecken (passiert oft bei JSON APIs)
      // Wir suchen solange, bis wir eine 'hash_id' oder 'id' finden
      let item = rawData;
      // source_document vor dem Unwrapping retten (liegt auf Wrapper-Ebene)
      const topLevelSourceDoc = item.source_document || null;
      
      // Level 1 Unpacking
      if (item.data && !item.hash_id && !item.id) item = item.data;
      // Level 2 Unpacking (falls doppelt verschachtelt)
      if (item.data && !item.hash_id && !item.id) item = item.data;

      // source_document zurückmergen falls noch nicht im Element selbst
      if (topLevelSourceDoc && !item.source_document) {
        item = { ...item, source_document: topLevelSourceDoc };
      }

      // URL Normalisierung: Falls url_element fehlt, schauen wir nach Alternativen
      if (!item.url_element) {
        if (item.url) item.url_element = item.url;
        else if (item.video_url) item.url_element = item.video_url;
        else if (item.src) item.url_element = item.src;
      }
      
      // Thumbnail Fallback (nur zur Sicherheit)
      if (!item.url_thumbnail) {
         if (item.thumbnail) item.url_thumbnail = item.thumbnail;
         else if (item.image) item.url_thumbnail = item.image;
      }
      
      return item;
    };

    (async () => {
      try {
        if (id.startsWith('col-')) {
          const res = await getCollectionByHash(id);
          // Collection Logik
          let data = res?.data || {};
          if (data.data && !data.elements) data = data.data; 

          const elements = Array.isArray(data.elements) ? data.elements : [];
          // Auch Elemente in Collections normalisieren
          const cleanElements = elements.map(normalizeLesson).filter(Boolean);
          
          if (cleanElements.length > 0) setLessons(cleanElements);
          
        } else if (id.startsWith('el-')) {
          const res = await getElementByHash(id);
          
          // Rohdaten holen
          const rawElem = res?.data || res;
          
          // Normalisieren mit der neuen Helfer-Funktion
          const cleanElem = normalizeLesson(rawElem);

          if (cleanElem && (cleanElem.id || cleanElem.hash_id)) {
            // Wenn bereits Lessons aus der Navigation vorhanden sind (Playlist aus Kurs etc.),
            // nur source_document des aktiven Elements nachrüsten – Playlist NICHT ersetzen.
            if (lessonsFromNav) {
              setLessons(prev => prev.map(l => {
                const lHash = l?.hash_id || l?.id;
                const cleanHash = cleanElem?.hash_id || cleanElem?.id;
                if (lHash === cleanHash || String(lHash) === String(cleanHash)) {
                  return { ...l, source_document: cleanElem.source_document ?? null };
                }
                // Alle anderen Lessons mit source_document: null markieren damit der Guard
                // beim nächsten Render nicht erneut feuert
                if (!('source_document' in l)) {
                  return { ...l, source_document: null };
                }
                return l;
              }));
            } else {
              // Echter Direktzugriff: nur dieses eine Element laden
              console.log("[HEJVI DEBUG] Loaded Single Element:", cleanElem);
              setLessons([cleanElem]);

              // Autoplay Versuch für Direct-Load (manchmal blockt Browser beim ersten Render)
              if (isAutoplayEnabled) {
                setTimeout(() => {
                  const vid = videoRefs.current[0];
                  if (vid) vid.play().catch(() => {});
                }, 500);
              }
            }
          } else {
             console.warn("[HEJVI DEBUG] Element loaded but structure unknown:", rawElem);
          }
        }
      } catch (err) {
          console.error("[ElementFeed] Load Error:", err);
      }
    })();
  }, [hashId, lessons.length, location?.search, isAutoplayEnabled]);

  useEffect(() => {
    const elements = videoRefs.current.filter(Boolean);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let pending = null;
        entries.forEach((entry) => {
          const vid = entry.target;
          if (!(vid instanceof HTMLVideoElement)) return;
          const idxAttr = vid.getAttribute('data-idx');
          const idxNum = Number(idxAttr);
          if (entry.isIntersecting && entry.intersectionRatio > 0.6 && Number.isFinite(idxNum)) {
            if (pending) clearTimeout(pending);
            pending = setTimeout(() => {
              if (!navigatingRef.current) {
                // Sofort alle anderen Videos pausieren, bevor React re-rendert
                videoRefs.current.forEach((vid, i) => {
                  if (i !== idxNum && vid instanceof HTMLVideoElement && !vid.paused) {
                    try { vid.pause(); } catch (_) {}
                  }
                });
                setActiveIndex(idxNum);
              }
            }, 100);
          }
        });
      },
      { threshold: [0, 0.6, 1] }
    );

    elements.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, [lessons.length]);

  function Sleep(milliseconds) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  useEffect(() => {
    const activeVideo = videoRefs.current[activeIndex];
    const activeIsVideo = activeVideo instanceof HTMLVideoElement;

    videoRefs.current.forEach((vid, i) => {
      if (!(vid instanceof HTMLVideoElement)) return;
      try {
        if (i === activeIndex && activeIsVideo) {
          // Nur abspielen wenn das aktive Element wirklich ein Video ist (kein Quiz/Challenge)
          if (isAutoplayEnabled) {
            vid.currentTime = 0;
            vid.play().catch(() => {});
          }
        } else {
          // Alle anderen IMMER pausieren – auch wenn activeIndex auf eine Challenge zeigt
          vid.pause();
        }
      } catch (_) {}
    });
  }, [activeIndex, isAutoplayEnabled]);

  // Analyze dynamic timers when lessons are loaded or updated
  useEffect(() => {
    if (Array.isArray(lessons) && lessons.length > 0) {
      console.log('[HEJVI DEBUG] Lessons loaded, analyzing collection timers');
      // Analyze timer settings for debugging purposes
      analyzeCollectionTimers(lessons);
    }
  }, [lessons, analyzeCollectionTimers]);

  // Make timer analysis function available globally for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.hejviAnalyzeTimers = manualAnalyzeTimers;
      console.log('[HEJVI DEBUG] Global timer analysis function available as window.hejviAnalyzeTimers()');
      window.hejviSetupCollectionTimers = (videoElements) => {
        try {
          if (!Array.isArray(videoElements)) return false;
          const next = {};
          // Build a fast map from DOM id to timer value for quick checks
          const domIdToTimer = {};
          videoElements.forEach((el) => {
            const key = String(el?.id ?? el?.hash_id ?? '');
            if (!key) return;
            // Accept both numeric 0/1 and boolean true/false; treat truthy as 1, falsy as 0
            const raw = el?.timer_on_end;
            const normalized = Number(raw === true ? 1 : raw === false ? 0 : raw);
            if (Number.isFinite(normalized)) {
              next[key] = normalized;
              const domId = el?.dom_id || key || null;
              if (domId) domIdToTimer[String(domId)] = normalized;
              // Store by DOM id as well so runtime can resolve by the actual element id attribute
              if (domId) next[String(domId)] = normalized;
            }
          });
          timerOverridesRef.current = next;
          console.log('[HEJVI DEBUG] hejviSetupCollectionTimers applied overrides:', next);
          // Also attach vanilla behavior à la setupCollectionTimers
          Object.entries(domIdToTimer).forEach(([domId, val]) => {
            const v = document.getElementById(domId);
            if (!v) return;
            try { v.onended = null; } catch (_) {}
            if (Number(val) === 1) {
              v.onended = () => {
                console.log(`[DEBUG] Timer triggered at the end of video: ${domId}`);
              };
            } else {
              console.log(`[DEBUG] No timer for video: ${domId} (TIMER_ON_END = 0)`);
            }
          });
          return true;
        } catch (e) {
          console.log('[HEJVI DEBUG] hejviSetupCollectionTimers error:', e);
          return false;
        }
      };
      // compatibility alias for your required function name
      window.setupCollectionTimers = window.hejviSetupCollectionTimers;
      // Progress tracking is now handled by setProgress API
      // No need for runtime URL/header overrides
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.hejviAnalyzeTimers;
        delete window.hejviSetupCollectionTimers;
        delete window.setupCollectionTimers;
        delete window.hejviSetProgressUrl;
        delete window.hejviSetProgressHeaders;
      }
    };
  }, [manualAnalyzeTimers]);

  const onToggleFullscreenSim = () => {
    setIsSimFullscreen(!isSimFullscreen);
  };

  const onToggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    localStorage.setItem('hejvi_video_muted', next.toString());
    applyMuteState(next);
  };

  const onTogglePlayPause = (idx) => {
    const vid = videoRefs.current[idx];
    if (!vid) return;

    if (vid.paused) {
      vid.play().catch(() => {});
      setIsVideoPaused(false);
    } else {
      try {
        vid.pause();
        setIsVideoPaused(true);
      } catch (_) {}
    }
  };

  // Spacebar → Play/Pause toggle
  // Guard: kein Trigger wenn Input/Textarea/ContentEditable fokussiert (z.B. Challenge-Textfelder)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== 'Space') return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
      e.preventDefault(); // verhindert Page-Scroll durch Space
      onTogglePlayPause(activeIndex);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex]); // onTogglePlayPause ist stabil (nur refs + setState)

  const onReplay = (idx) => {
    const vid = videoRefs.current[idx];
    if (!vid) return;

    // Prüfen, ob HLS.js für dieses Video aktiv ist
    const hls = hlsInstancesRef.current[idx];

    try { vid.pause(); } catch (_) {}
    try { vid.onended = null; } catch (_) {}
    
    // Zurückspulen
    try { vid.currentTime = 0; } catch (_) {}
    
    // FIX: vid.load() bricht HLS-Streams (Blob-URLs) in Chrome.
    // Wir führen es daher NUR aus, wenn KEIN HLS aktiv ist (z.B. Safari Native oder MP4 Fallback).
    if (!hls) {
        try { if (vid.load) vid.load(); } catch (_) {}
    }

    try { vid.muted = isMuted; } catch (_) {}
    
    const playPromise = (() => { try { return vid.play(); } catch (_) { return undefined; } })();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch((e) => {
        // Retry muted if autoplay is blocked on iOS
        try { vid.muted = true; } catch (_) {}
        try { vid.play(); } catch (_) {}
      });
    }
    setIsVideoPaused(false);
    setShowEndPopup(false);
  };

  const onSkipBackward = (idx) => {
    const vid = videoRefs.current[idx];
    if (!vid) return;

    vid.currentTime = Math.max(0, vid.currentTime - 10);
  };

  const onSkipForward = (idx) => {
    const vid = videoRefs.current[idx];
    if (!vid) return;

    vid.currentTime = Math.min(vid.duration || 0, vid.currentTime + 10);
  };

  const onProgressBarClick = (idx, event) => {
    event.stopPropagation();
    const vid = videoRefs.current[idx];
    if (!vid || !vid.duration) return;

    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * vid.duration;

    vid.currentTime = Math.max(0, Math.min(vid.duration, newTime));
  };

  const startScrub = (idx, clientX, target, rawEvent) => {
    try { rawEvent?.stopPropagation(); rawEvent?.preventDefault(); } catch (_) {}
    const vid = videoRefs.current[idx];
    if (!vid || !vid.duration) return;
    const rect = target.getBoundingClientRect();
    scrubRectRef.current = rect;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setScrubState({ active: true, idx, pct });
    try { vid.currentTime = pct * vid.duration; } catch (_) {}

    const onMove = (e) => {
      try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
      const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const r = scrubRectRef.current;
      if (!r) return;
      const p = Math.max(0, Math.min(1, (x - r.left) / r.width));
      setScrubState((s) => ({ ...s, pct: p }));
      try { vid.currentTime = p * vid.duration; } catch (_) {}
    };
    const onEnd = (e) => {
      try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove, { passive: false });
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchend', onEnd);
      setScrubState((s) => ({ ...s, active: false }));
      scrubRectRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchend', onEnd);
  };

  const onProgressHoverMove = (idx, clientX, target) => {
    const rect = target.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setHoverState({ active: true, idx, pct });
  };

  const onProgressHoverLeave = () => {
    setHoverState({ active: false, idx: -1, pct: 0 });
  };

  const playRandomVideo = useCallback(() => {
    const total = (lessons || []).length;
    if (total === 0) return;

    let randomIdx;
    do {
      randomIdx = Math.floor(Math.random() * total);
    } while (randomIdx === activeIndex && total > 1);

    console.log('[HEJVI DEBUG] Playing random video at index:', randomIdx);

    if (randomIdx !== activeIndex) {
      setActiveIndex(randomIdx);
      setTimeout(() => {
        const randomSection = videoRefs.current[randomIdx]?.closest('[data-snap-section]');
        if (randomSection) {
          try {
            randomSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } catch (_) {}
        }
      }, 100);
    }
  }, [activeIndex, lessons]);

  const getRandomLessonVideoUrl = useCallback(() => {
    const pool = (lessons || []).filter((l, i) => Number(l?.type) !== 3 && i !== activeIndex && l?.url_element);
    if (pool.length === 0) {
      const anyPool = (lessons || []).filter((l, i) => i !== activeIndex && l?.url_element);
      if (anyPool.length === 0) return '';
      const pick = anyPool[Math.floor(Math.random() * anyPool.length)];
      return normalizeMediaUrl(pick.url_element);
    }
    const pick = pool[Math.floor(Math.random() * pool.length)];
    return normalizeMediaUrl(pick.url_element);
  }, [lessons, activeIndex]);

  const onNextVideo = useCallback(() => {
    const total = (lessons || []).length;
    if (total === 0) return;

    let targetIdx = activeIndex + 1;
    
    // Wenn wir am Ende der Playlist sind, nichts tun
    if (targetIdx >= total) {
      setShowEndPopup(false);
      return;
    }

    // --- FIX: Absturz verhindern, wenn kein Video da ist (z.B. bei Quiz) ---
    const currentVideo = videoRefs.current[activeIndex];
    // Wir prüfen sicherheitshalber, ob currentVideo existiert UND eine .pause Funktion hat
    if (currentVideo && typeof currentVideo.pause === 'function') {
        try { 
            currentVideo.pause(); 
        } catch (e) {
            console.log('[HEJVI DEBUG] Error pausing previous video:', e);
        }
    }
    // -----------------------------------------------------------------------

    console.log('[HEJVI DEBUG] Navigating from index', activeIndex, 'to', targetIdx);
    navigatingRef.current = true;
    setActiveIndex(targetIdx);

    // Navigation erzwingen (Scrollen)
    const forceNavigation = () => {
      const nextSection = videoRefs.current[targetIdx]?.closest('[data-snap-section]');
      
      // Fall 1: Sektion gefunden
      if (nextSection) {
        try {
          nextSection.scrollIntoView({ behavior: 'instant', block: 'start' });
          return true;
        } catch (e) {}
      }

      // Fall 2: Video Ref gefunden
      const nextVideo = videoRefs.current[targetIdx];
      if (nextVideo) {
        try {
          nextVideo.scrollIntoView({ behavior: 'instant', block: 'start' });
          return true;
        } catch (e) {}
      }

      // Fall 3: Manuell über Container scrollen
      if (containerRef.current) {
        try {
          const sectionHeight = window.innerHeight;
          const targetScrollTop = targetIdx * sectionHeight;
          containerRef.current.scrollTo({ top: targetScrollTop, behavior: 'instant' });
          return true;
        } catch (e) {}
      }
      return false;
    };

    if (!forceNavigation()) {
      setTimeout(() => forceNavigation(), 100);
    }
    
    setTimeout(() => { navigatingRef.current = false; }, 500);
    setShowEndPopup(false);
  }, [activeIndex, lessons]);

    //let targetIdx = -1;
   

  /**
   * Play challenge video based on user answer
   */
  const playChallengeVideo = useCallback(async ({
    isYesCorrect,
    idx,
    lesson,
    correctElementId,
    incorrectElementId,
    correctHash,
    incorrectHash
  }) => {
    console.log('[HEJVI DEBUG] Challenge hash values:', {
      correctHash,
      incorrectHash,
      lessonName: lesson?.name,
      lessonId: lesson?.id,
      lessonHashId: lesson?.hash_id
    });

    const setSuccessState = (url, targetId = null, hash = null) => {
      setChallengeState((p) => ({
        ...p,
        [idx]: {
          status: 'success',
          playing: 'success',
          targetElementId: targetId,
          targetHash: hash,
          randomUrl: url
        }
      }));
    };

    const setFailureState = (url, targetId = null, hash = null) => {
      setChallengeState((p) => ({
        ...p,
        [idx]: {
          status: 'failure',
          playing: 'failure',
          targetElementId: targetId,
          targetHash: hash,
          randomUrl: url
        }
      }));
    };

    try {
      // Immediately reflect intent in UI so overlay appears without waiting for network
      setChallengeState((p) => ({
        ...p,
        [idx]: {
          status: isYesCorrect ? 'success' : 'failure',
          playing: isYesCorrect ? 'success' : 'failure',
          targetElementId: null,
          targetHash: null,
          randomUrl: null
        }
      }));
      // Fresh run: ensure overlay 'ended' guard is reset even if URL is identical to last time
      try { overlayEndedOnceRef.current[idx] = false; } catch (_) {}

      if (isYesCorrect) {
        if (correctElementId && 1==2) {
          console.log('[HEJVI DEBUG] Correct answer - Fetching video for element ID:', correctElementId);
          const url = await fetchElementVideo(correctElementId);
          if (url) setSuccessState(url, correctElementId);
          else setSuccessState(null);
        } else if (correctHash) {
          //console.log('[HEJVI DEBUG] Correct answer - Using hash:', correctHash);
          const res = await fetchResponseVideo(correctHash);
          const url = res?.url || '';
          if (url) setSuccessState(url, null, correctHash);
          else setSuccessState(null);
        } else {
          console.log('[HEJVI DEBUG] Correct answer - No video, moving to next');
          setSuccessState(null);
          if (activeIndex === idx) onNextVideo();
        }
      } else {
        if (incorrectElementId && 1==2) {
          //console.log('[HEJVI DEBUG] Incorrect answer - Fetching video for element ID:', incorrectElementId);
          const url = await fetchElementVideo(incorrectElementId);
          if (url) setFailureState(url, incorrectElementId);
          else setFailureState(null);
        } else if (incorrectHash) {
          console.log('[HEJVI DEBUG] Incorrect answer - Using hash:', incorrectHash);
          const res = await fetchResponseVideo(incorrectHash);
          const url = res?.url || '';
          if (url) setFailureState(url, null, incorrectHash);
          else setFailureState(null);
        } else {
          // No incorrectElementId and no incorrectHash: do NOT play random content.
          // Show failure controls immediately without overlay playback.
          console.log('[HEJVI DEBUG] Incorrect answer - No video available; showing failure controls');
          setChallengeState((p) => ({
            ...p,
            [idx]: {
              status: 'failure',
              playing: null,
              targetElementId: null,
              targetHash: null,
              randomUrl: null
            }
          }));
        }
      }
    } catch (error) {
      console.error('[HEJVI DEBUG] Error handling challenge video:', error);
    }
  }, [activeIndex, fetchElementVideo, fetchResponseVideo, getRandomLessonVideoUrl, onNextVideo]);

  const handleQRScan = useCallback(async (decodedText) => {
    const idx = qrPendingIndexRef.current;

    // --- Kontext: QR-Scan ohne zugehörige Challenge (idx == null) ---
    // Das passiert z.B. wenn ein User einen beliebigen QR-Code scannt,
    // der nicht Teil einer Challenge ist. Wir prüfen dann, ob der Code
    // einer bekannten Collection / einem bekannten Element entspricht.
    if (idx == null) {
      setIsQRScannerOpen(false);
      const scannedStr = String(decodedText || '').trim();

      // Versuche, den Hash aus der gescannten URL oder dem Rohwert zu extrahieren
      let extractedHash = scannedStr;
      try {
        const url = new URL(scannedStr);
        // Hash ist typischerweise das letzte Pfadsegment, z.B. /col-abc123
        const pathParts = url.pathname.replace(/\/$/, '').split('/');
        const lastPart = pathParts[pathParts.length - 1];
        if (lastPart && (lastPart.startsWith('col-') || lastPart.startsWith('el-'))) {
          extractedHash = lastPart;
        } else if (url.searchParams.get('hash')) {
          extractedHash = url.searchParams.get('hash');
        } else if (url.hash && url.hash.length > 1) {
          extractedHash = url.hash.slice(1);
        }
      } catch (_) {
        // decodedText war keine URL – Rohwert bleibt erhalten
      }

      // ── Bekannte Collection / Element direkt navigieren ──────────────────
      const isKnownHash = extractedHash.startsWith('col-') || extractedHash.startsWith('el-');
      if (isKnownHash) {
        navigate(`/${extractedHash}`);
        return;
      }

      // ── Unbekannter Rohwert: Präfix-Varianten im Backend nachschlagen ────
      // Der gescannte Code könnte zu einem Objekt gehören, das als
      // "col-{code}" oder "el-{code}" gespeichert ist. Wir prüfen beide.
      try {
        const colHash = `col-${extractedHash}`;
        const elHash  = `el-${extractedHash}`;

        // Parallele Lookups – welcher zuerst antwortet (ohne Fehler) gewinnt
        const [colResult, elResult] = await Promise.allSettled([
          getCollectionByHash(colHash),
          getElementByHash(elHash),
        ]);

        if (colResult.status === 'fulfilled' && colResult.value) {
          navigate(`/${colHash}`);
          return;
        }
        if (elResult.status === 'fulfilled' && elResult.value) {
          navigate(`/${elHash}`);
          return;
        }
      } catch (_) {
        // Lookup-Fehler sind unkritisch – weiter zum Modal
      }

      // ── Wirklich unbekannt → Modal anzeigen ──────────────────────────────
      setUnknownQRModal({ isOpen: true, scannedCode: scannedStr });
      qrPendingIndexRef.current = null;
      return;
    }

    // --- Kontext: QR-Scan als Challenge-Antwort (idx gesetzt) ---
    try {
      if (idx < 0 || idx >= lessons.length) {
        setIsQRScannerOpen(false);
        qrPendingIndexRef.current = null;
        return;
      }
      const lesson = lessons[idx];
      const userScan = String(decodedText || '').trim().toLowerCase();
      const expected = String(lesson?.correct_option || '').trim().toLowerCase();
      const isCorrect = expected && userScan === expected;
      
      try {
        await setChallenge({
          challenge_id: Number(lesson?.id || idx),
          answer_id: userScan,
          correct_answer_id: expected,
          help_used: Number(!!hintHighlights[idx])
        });
        if (isCorrect) await awardQuizXP(lesson);
      } catch (_) {}

      await playChallengeVideo({
        isYesCorrect: isCorrect,
        idx,
        lesson,
        correctElementId: lesson?.element_correct_id,
        incorrectElementId: lesson?.element_incorrect_id,
        correctHash: lesson?.response_correct_hash,
        incorrectHash: lesson?.response_incorrect_hash
      });
    } catch (_) {}
    setIsQRScannerOpen(false);
    qrPendingIndexRef.current = null;
  }, [lessons, playChallengeVideo, hintHighlights, navigate]);

  useEffect(() => {
    if (singleTapTimeout.current) {
      clearTimeout(singleTapTimeout.current);
      singleTapTimeout.current = null;
    }
    setShowEndPopup(false);
  }, [activeIndex]);



  useEffect(() => {
    const vid = videoRefs.current[activeIndex];
    if (!vid) return;

    const onTime = () => {
      setCurrentTime(vid.currentTime || 0);
      setDuration(vid.duration || 0);
      // Progress tracking is now handled by startProgressInterval
      // This function only updates the UI state
    };

    const onLoaded = () => {
      setDuration(vid.duration || 0);
      
      // Reset chapter seek flag if this is a different video
      const currentVideoHash = lessons[activeIndex]?.hash_id;
      if (lastVideoHashRef.current !== currentVideoHash) {
        hasAppliedChapterSeekRef.current = false;
        lastVideoHashRef.current = currentVideoHash;
        console.log(`🔄 New video detected: ${currentVideoHash}, resetting seek flag`);
      }
      
      // Handle chapter start time first (highest priority)
      if (!hasAppliedChapterSeekRef.current && activeIndex === startIndex && chapterStartTime && Number(chapterStartTime) > 0) {
        vid.currentTime = Math.min(Number(chapterStartTime), vid.duration || 0);
        hasAppliedChapterSeekRef.current = true;
        console.log(`📖 Chapter start time applied: ${chapterStartTime}s`);
        return;
      }
      
      // Handle video resume logic
      if (!hasAppliedChapterSeekRef.current && vid.duration > 0) {
        const lesson = lessons[activeIndex];
        const progress = parseFloat(lesson?.progress || 0);
        
        if (progress >= 10) {
          // Restart from beginning if >=20% complete
          vid.currentTime = 0;
          console.log(`Progress >= 10% (${progress}%), starting from beginning`);
          hasAppliedChapterSeekRef.current = true;
        } else if (progress > 0) {
          // Resume from saved progress
          const resumeTime = (progress / 100) * vid.duration;
          //vid.currentTime = Math.min(resumeTime, vid.duration);
          // DK EDIT
          vid.currentTime = 0;
          console.log(`Starting at progress point: ${progress}% (${resumeTime}s)`);
          hasAppliedChapterSeekRef.current = true;
        } else {
          // Video not started yet, start from beginning
          vid.currentTime = 0;
          console.log(`Starting video from beginning (0% progress)`);
          hasAppliedChapterSeekRef.current = true;
        }
      }
    };

    const onPlay = () => {
      setIsVideoPaused(false);
      const ls = lessons[activeIndex];
      sendPlaybackProgress({ event: 'play', id: ls?.id ?? null, hash_id: ls?.hash_id ?? null, name: ls?.name ?? ls?.title ?? null, index: activeIndex, current: Math.floor(vid.currentTime || 0), duration: Math.floor(vid.duration || 0), paused: false, ts: Date.now() });
      startProgressInterval(vid, ls, activeIndex);
    };

    const onPause = () => {
      setIsVideoPaused(true);
      const ls = lessons[activeIndex];
      sendPlaybackProgress({ event: 'pause', id: ls?.id ?? null, hash_id: ls?.hash_id ?? null, name: ls?.name ?? ls?.title ?? null, index: activeIndex, current: Math.floor(vid.currentTime || 0), duration: Math.floor(vid.duration || 0), paused: true, ts: Date.now() });
      stopProgressInterval();
    };

    const onEnded = () => {
      setIsVideoPaused(true);
      const lesson = lessons[activeIndex];
      // Don't update progress to 100% when video ends - keep the last saved progress
      // sendPlaybackProgress({ event: 'ended', id: lesson?.id ?? null, hash_id: lesson?.hash_id ?? null, name: lesson?.name ?? lesson?.title ?? null, index: activeIndex, current: Math.floor(vid.duration || 0), duration: Math.floor(vid.duration || 0), paused: true, ts: Date.now() });
      stopProgressInterval();
      const activeVideo = videoRefs.current[activeIndex];
      const activeDomId = activeVideo && activeVideo.id ? String(activeVideo.id) : null;
      console.log('[HEJVI DEBUG] Video ended (integrated handler)', {
        activeIndex,
        lesson: lesson?.title || lesson?.name,
        lessonType: lesson?.type
      });

      // If we came here from a replay flow, suppress timer and advance to next video automatically
      if (returnToChallenge && Number(lesson?.type) !== 3) {
        console.log('[HEJVI DEBUG] Finished replay target, advancing to next video (no timer)');
        setReturnToChallenge(null);
        onNextVideo();
        return;
      }
      // Respect optional timer overrides if provided via hejviSetupCollectionTimers; otherwise fall back to lesson.timer_on_end (API)
      const overrideVal =
        (activeDomId && timerOverridesRef.current?.[activeDomId]) ??
        timerOverridesRef.current?.[String(lesson?.id)] ??
        timerOverridesRef.current?.[String(lesson?.hash_id)] ??
        timerOverridesRef.current?.[String(lesson?.hash_id || (lesson?.id != null ? `video-${lesson.id}` : ''))];
      const baseTimer = Number(lesson?.timer_on_end ?? 1);
      const effectiveTimer = Number.isFinite(Number(overrideVal)) ? Number(overrideVal) : baseTimer;
      if (effectiveTimer === 0) {
        console.log('[HEJVI DEBUG] Override timer_on_end=0 → advancing immediately');
        onNextVideo();
      } else {
        console.log('[HEJVI DEBUG] Showing 3s countdown for non-quiz video end');
        setShowEndPopup(true);
        setEndPopupTimer(3);
      }
    };

    vid.addEventListener('timeupdate', onTime);
    vid.addEventListener('loadedmetadata', onLoaded);
    vid.addEventListener('play', onPlay);
    vid.addEventListener('pause', onPause);
    let endedFired = false;
    const endedHandler = () => {
      if (endedFired) return; // guard duplicate 'ended' on iOS
      endedFired = true;
      onEnded();
    };
    vid.addEventListener('ended', endedHandler);

    applyMuteState(isMuted);

    return () => {
      vid.removeEventListener('timeupdate', onTime);
      vid.removeEventListener('loadedmetadata', onLoaded);
      vid.removeEventListener('play', onPlay);
      vid.removeEventListener('pause', onPause);
      vid.removeEventListener('ended', endedHandler);
    };
  }, [activeIndex, isMuted, lessons, chapterStartTime, returnToChallenge, onNextVideo, startIndex, applyMuteState]);

  // Send an 'exit' event with last position when switching away from a video
  useEffect(() => {
    const prevIdx = prevActiveIndexRef.current;
    if (prevIdx != null && prevIdx >= 0 && prevIdx !== activeIndex) {
      const vid = videoRefs.current[prevIdx];
      // dk edit pause
      //alert ("Exitted Video");
      const ls = lessons[prevIdx];
      if (vid && ls) {
        try {
          sendPlaybackProgress({
            event: 'exit',
            id: ls?.id ?? null,
            hash_id: ls?.hash_id ?? null,
            name: ls?.name ?? ls?.title ?? null,
            index: prevIdx,
            current: Math.max(0, Math.floor(vid.currentTime || 0)),
            duration: Math.max(0, Math.floor(vid.duration || 0)),
            paused: !!vid.paused,
            ts: Date.now()
          });
        } catch (_) {}
      }
    }
    prevActiveIndexRef.current = activeIndex;
  }, [activeIndex, lessons, sendPlaybackProgress]);

  // Flush 'exit' on tab close / visibility change
  useEffect(() => {
    const flushExit = () => {
      stopProgressInterval();
      const idx = activeIndex;
      const vid = videoRefs.current[idx];
      const ls = lessons[idx];
      if (vid && ls) {
        try {
          sendPlaybackProgress({
            event: 'exit',
            id: ls?.id ?? null,
            hash_id: ls?.hash_id ?? null,
            name: ls?.name ?? ls?.title ?? null,
            index: idx,
            current: Math.max(0, Math.floor(vid.currentTime || 0)),
            duration: Math.max(0, Math.floor(vid.duration || 0)),
            paused: !!vid.paused,
            ts: Date.now()
          });
        } catch (_) {}
      }
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushExit(); };
    window.addEventListener('beforeunload', flushExit);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopProgressInterval();
      window.removeEventListener('beforeunload', flushExit);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeIndex, lessons, sendPlaybackProgress, stopProgressInterval]);

  useEffect(() => {
    if (!showEndPopup) return;
    if (endPopupTimer > 0) {
      const timer = setTimeout(() => {
        setEndPopupTimer(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (endPopupTimer === 0) {
      console.log('[HEJVI DEBUG] Timer expired');
      setShowEndPopup(false);
      setEndPopupTimer(3);
      setTimeout(() => {
        if (returnToChallenge) {
          console.log('[HEJVI DEBUG] Replay completed, going to next video instead of returning to challenge');
          setReturnToChallenge(null);
          onNextVideo();
        } else {
          onNextVideo();
        }
      }, 50);
    }
  }, [showEndPopup, endPopupTimer, onNextVideo, returnToChallenge, lessons]);

  useEffect(() => {
    if (isSimFullscreen) {
      document.body.classList.add('fullscreen-mode');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('fullscreen-mode');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('fullscreen-mode');
      document.body.style.overflow = '';
    };
  }, [isSimFullscreen]);

  useEffect(() => {
    return () => {
      if (singleTapTimeout.current) {
        clearTimeout(singleTapTimeout.current);
      }
    };
  }, []);
  
  // --------------------------------------------------------
  // HELPER: HLS Polling nach Manifest-Fehler
  // Startet ein Intervall das alle 5s HEAD auf hlsUrl macht.
  // Wenn OK → Spinner entfernen + HLS neu initialisieren.
  // Wird sowohl vom HLS-Error-Handler als auch vom proaktiven
  // Rendering-Check genutzt.
  // --------------------------------------------------------
  const startHlsPolling = useCallback((idx, hlsUrl) => {
    if (renderingPollRef.current[idx]) return; // Guard: kein Doppel-Polling
    renderingPollRef.current[idx] = setInterval(async () => {
      try {
        const token = localStorage.getItem('hejvi_access_token') || '';
        const res = await fetch(hlsUrl, {
          method: 'HEAD',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          clearInterval(renderingPollRef.current[idx]);
          delete renderingPollRef.current[idx];
          setRenderingElements(prev => {
            const next = { ...prev };
            delete next[idx];
            return next;
          });
          console.log(`[HEJVI VIDEO] Stream bereit für idx=${idx} — HLS wird neu geladen`);
          setTimeout(() => {
            const vid = videoRefs.current[idx];
            if (!vid || hlsInstancesRef.current[idx]) return;
            import('hls.js').then((mod) => {
              const Hls = mod.default || mod;
              if (!Hls?.isSupported()) return;
              const hls2 = new Hls({
                enableWorker: true,
                autoStartLoad: false,
                subtitleDisplay: false,
                xhrSetup: (xhr) => {
                  const t = localStorage.getItem('hejvi_access_token');
                  if (t) xhr.setRequestHeader('Authorization', `Bearer ${t}`);
                }
              });
              hlsInstancesRef.current[idx] = hls2;
              hls2.attachMedia(vid);
              hls2.on(Hls.Events.MEDIA_ATTACHED, () => hls2.loadSource(hlsUrl));
              hls2.on(Hls.Events.MANIFEST_PARSED, (_, d) => {
                applyAudioTrackPreference(vid, hls2, d);
                hls2.startLoad(-1);
                if (idx === activeIndex) vid.play().catch(() => {});
              });
              hls2.on(Hls.Events.ERROR, (_, d2) => {
                if (d2.fatal) { hls2.destroy(); delete hlsInstancesRef.current[idx]; }
              });
            });
          }, 300);
        }
      } catch (_) { /* Netzwerkfehler → weiter pollen */ }
    }, 5000);
  }, [activeIndex, applyAudioTrackPreference]);

  // --------------------------------------------------------
  // FIX: HLS.js Initialisierung (Chrome/Firefox)
  // STRATEGIE: "Sandwich-Methode" (Start -> Switch)
  // --------------------------------------------------------

  // --------------------------------------------------------
  // FIX: HLS & Audio-Sprache für Reaktionsvideos (Overlay)
  // MIT MP4 FALLBACK
  // --------------------------------------------------------
  useEffect(() => {
    // 1. Prüfen, ob ein Overlay aktiv ist
    const activeOverlayIdx = Object.keys(challengeState).find(k => challengeState[k]?.playing);
    if (!activeOverlayIdx) return;

    const state = challengeState[activeOverlayIdx];
    const video = overlayVideoRef.current;

    // 2. Original URL ermitteln (meistens MP4)
    let rawUrl = '';
    if (state.targetHash && responseVideos[state.targetHash]?.url) {
        rawUrl = responseVideos[state.targetHash].url;
    } else if (state.targetElementId) {
        const target = lessons.find(l => Number(l.id) === Number(state.targetElementId) || l.hash_id === state.targetElementId);
        if (target) rawUrl = normalizeMediaUrl(target.url_element);
    } else if (state.randomUrl) {
        rawUrl = normalizeMediaUrl(state.randomUrl);
    }

    if (!video || !rawUrl) return;

    // Helper: Untertitel aktivieren
    const activateOverlaySubtitles = () => {
        if (!video.textTracks) return;
        setTimeout(() => {
            const tracks = Array.from(video.textTracks);
            if (tracks.length === 0) return;

            if (!showSubtitles) {
                tracks.forEach(t => t.mode = 'hidden');
                return;
            }

            const targetLang = currentLanguage ? String(currentLanguage).split('-')[0].toLowerCase() : 'de';
            let best = tracks.find(t => t.language === targetLang);
            if (!best) best = tracks.find(t => t.language === 'en');
            if (!best) best = tracks[0];

            tracks.forEach(t => t.mode = (t === best) ? 'showing' : 'hidden');
        }, 100);
    };

    // 3. HLS Kandidaten erstellen (Versuch auf .m3u8)
    // Wir merken uns aber 'rawUrl' für den Fallback!
    let hlsUrl = getSecureMediaUrl(rawUrl);
    if (hlsUrl.includes('.mp4')) {
        // .m3u8 nur im Pfad-Teil ersetzen, ?token= bleibt erhalten
        hlsUrl = hlsUrl.replace('.mp4', '.m3u8');
    }

    // 4. Safari / Native Check
    const ua = navigator.userAgent;
    const isRealSafari = ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Android');
    
    if (isRealSafari) {
        // Safari versucht nativ HLS (.m3u8). Kein hls.js nötig.
        // ABER: Wenn das Manifest kein #EXT-X-ENDLIST hat, setzt Safari duration=Infinity.
        // Daher lauschen wir auf loadedmetadata und prüfen die duration.
        const handleSafariOverlayMetadata = () => {
            if (!isFinite(video.duration) && video.src && video.src.includes('.m3u8')) {
                console.log(`[HEJVI OVERLAY SAFARI] duration=Infinity → Fallback auf MP4`);
                const mp4Url = getSecureMediaUrl(rawUrl.replace('.m3u8', '.mp4'));
                video.removeEventListener('loadedmetadata', handleSafariOverlayMetadata);
                video.src = mp4Url;
                video.load();
                video.play().catch(() => {});
            }
        };
        video.addEventListener('loadedmetadata', handleSafariOverlayMetadata, { once: true });
        applyAudioTrackPreference(video, null);
        return;
    }

    // 5. HLS.js für Chrome/Firefox initialisieren
    import('hls.js').then((mod) => {
        const Hls = mod.default || mod;
        if (Hls && Hls.isSupported()) {
            
            if (hlsInstancesRef.current['overlay']) {
                hlsInstancesRef.current['overlay'].destroy();
            }

            console.log(`[HEJVI OVERLAY] Attempting HLS: ${hlsUrl}`);

            const hls = new Hls({ 
                enableWorker: true, 
                lowLatencyMode: true,
                autoStartLoad: false,
                subtitleDisplay: false, // Wir rendern Untertitel selbst via VTT-Blob-<track>
                // Auth: ?token= ist bereits in hlsUrl (getSecureMediaUrl).
                // xhrSetup setzt zusätzlich Authorization-Header als Defense-in-Depth
                // falls der Proxy den Query-Parameter in einer zukünftigen Version entfernt.
                xhrSetup: (xhr) => {
                    const t = localStorage.getItem('hejvi_access_token');
                    if (t) xhr.setRequestHeader('Authorization', `Bearer ${t}`);
                }
            });
            
            hlsInstancesRef.current['overlay'] = hls;

            hls.attachMedia(video);
            
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(hlsUrl);
            });

            // SPRACHE SETZEN BEI ERFOLG
            hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                console.log(`[HEJVI OVERLAY] HLS found! Applying language: ${currentLanguage}`);
                const trackIdx = applyAudioTrackPreference(video, hls, data);
                hls.startLoad(-1);
                if (trackIdx !== -1) hls.audioTrack = trackIdx;
                video.play().catch(e => console.log("Overlay Autoplay blocked:", e));
                video.onloadeddata = () => {
                        activateOverlaySubtitles();
                    };
            });

            // --- WICHTIG: FALLBACK LOGIK ---
            hls.on(Hls.Events.ERROR, (event, data) => {
                // Wenn es ein Netzwerk-Fehler ist (z.B. 404 weil keine .m3u8 existiert)
                if (data.fatal || data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                    console.warn(`[HEJVI OVERLAY] HLS failed (${data.details}), falling back to MP4: ${rawUrl}`);
                    
                    // HLS zerstören, damit es nicht weiter versucht
                    hls.destroy();
                    delete hlsInstancesRef.current['overlay'];

                    // Hart auf MP4 setzen
                    video.src = rawUrl;
                    
                    video.load(); // Wichtig: Neu laden
                    video.play().catch(e => console.log("Fallback Play blocked", e));
                    video.onloadeddata = () => {
                        activateOverlaySubtitles();
                    };
                }
            });
        }
    });

    return () => {
        if (hlsInstancesRef.current['overlay']) {
            hlsInstancesRef.current['overlay'].destroy();
            delete hlsInstancesRef.current['overlay'];
        }
    };

  }, [challengeState, responseVideos, currentLanguage, lessons]);
  
  useEffect(() => {
    if (!lessons || lessons.length === 0) return;

    lessons.forEach((lesson, idx) => {
      const video = videoRefs.current[idx];
      if (!video || !lesson.url_element || hlsInstancesRef.current[idx]) return;

      // URL Setup — getSecureMediaUrl hängt ?token= an /media/clients/-Pfade
      const rawUrl   = normalizeMediaUrl(lesson.url_element);
      const mp4Src   = getSecureMediaUrl(rawUrl.replace('.m3u8', '.mp4'));
      const hlsSrc   = getSecureMediaUrl(rawUrl.replace('.mp4',  '.m3u8'));
      
      // Safari Check
      const ua = navigator.userAgent;
      const isRealSafari = ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Android');
      const canPlayNative = video.canPlayType('application/vnd.apple.mpegURL');

      if (isRealSafari && canPlayNative) {
         console.log(`[HEJVI VIDEO] Real Safari detected. Skipping hls.js.`);
         return;
      }

      console.log(`[HEJVI VIDEO] Initializing HLS.js for Lesson ${idx}...`);
      
      import('hls.js').then((mod) => {
          const Hls = mod.default || mod;
          if (Hls && Hls.isSupported()) {
              const hls = new Hls({ 
                  enableWorker: true, 
                  lowLatencyMode: true,
                  autoStartLoad: false, // WICHTIG: Manueller Start
                  subtitleDisplay: false, // Wir rendern Untertitel selbst via VTT-Blob-<track>
                  // Auth: ?token= ist in hlsSrc (getSecureMediaUrl), xhrSetup als
                  // Defense-in-Depth für .ts-Segment-Requests die eine neue URL aufbauen.
                  xhrSetup: (xhr) => {
                      const t = localStorage.getItem('hejvi_access_token');
                      if (t) xhr.setRequestHeader('Authorization', `Bearer ${t}`);
                  }
              });
              
              hls.attachMedia(video);
              
              hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                  hls.loadSource(hlsSrc);
              });

              hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                  console.log(`[HEJVI VIDEO] Manifest parsed! Tracks found:`, data.audioTracks?.length);
                  
                  // 1. Zuerst den Index ermitteln (ohne ihn zu setzen)
                  // Wir übergeben 'hls' noch nicht, damit die Funktion nicht selbst setzt,
                  // oder wir ignorieren, dass sie es tut.
                  const chosenTrack = applyAudioTrackPreference(video, hls, data);
                  console.log(`[HEJVI VIDEO] Computed Track Index: ${chosenTrack}`);

                  // 2. JETZT Starten! 
                  // Damit wird der interne State von hls.js initialisiert.
                  // Er wird kurzzeitig versuchen, "Default" (Deutsch) zu laden...
                  console.log(`[HEJVI VIDEO] Starting Load NOW (Synchronous)...`);
                  hls.startLoad(-1);

                  // 3. ...aber wir überschreiben es SOFORT (Synchron)
                  // Bevor der Request rausgeht, biegen wir das Gleis um.
                  if (chosenTrack !== -1) {
                      hls.audioTrack = chosenTrack;
                      console.log(`[HEJVI VIDEO] Force-Switched Audio Track to ${chosenTrack} IMMEDIATELY after startLoad`);
                  }
                  
                  // 4. Play
                  if (idx === activeIndex && !isQRScannerOpen) {
                      const p = video.play();
                      if (p) p.catch(e => {
                          console.log("Autoplay blocked:", e);
                          // Autoplay geblockt (Chrome-Policy) → Play-Button anzeigen
                          setIsVideoPaused(true);
                      });
                  }
              });

              hls.on(Hls.Events.ERROR, (event, data) => {
                  if (data.fatal) {
                      // Manifest-Fehler = Video noch im Rendering (FFmpeg läuft noch).
                      // Overlay anzeigen + alle 5s pollen bis stream_0.m3u8 verfügbar.
                      const isManifestError = data.type === Hls.ErrorTypes.NETWORK_ERROR
                          && (data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR
                              || data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT
                              || data.details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR);

                      if (isManifestError && !renderingPollRef.current[idx]) {
                          // ── MP4-Check: Altes Video (nur MP4, kein HLS) vs. wirklich noch rendernd ──
                          // Wenn die MP4 bereits existiert, ist es ein altes Video ohne HLS-Stream.
                          // In diesem Fall KEINEN Spinner zeigen, sondern direkt auf MP4 fallen.
                          const mp4CheckToken = localStorage.getItem('hejvi_access_token') || '';
                          fetch(mp4Src, {
                              method: 'HEAD',
                              headers: mp4CheckToken ? { Authorization: `Bearer ${mp4CheckToken}` } : {}
                          }).then(mp4Res => {
                              if (mp4Res.ok) {
                                  // MP4 vorhanden → altes Video, direkt abspielen
                                  console.log(`[HEJVI VIDEO] HLS fehlt aber MP4 ok für idx=${idx} → MP4 Fallback (altes Video)`);
                                  hls.destroy();
                                  delete hlsInstancesRef.current[idx];
                                  video.src = mp4Src;
                                  video.load();
                                  if (idx === activeIndex) video.play().catch(() => {});
                              } else {
                                  // MP4 auch nicht da → Video wird noch gerendert → Spinner + Polling
                                  console.log(`[HEJVI VIDEO] Manifest nicht verfügbar (FFmpeg läuft noch) für idx=${idx}`);
                                  hls.destroy();
                                  delete hlsInstancesRef.current[idx];
                                  setRenderingElements(prev => ({ ...prev, [idx]: true }));
                                  startHlsPolling(idx, getSecureMediaUrl(hlsSrc));
                              }
                          }).catch(() => {
                              // Netzwerkfehler beim MP4-Check → im Zweifel Spinner zeigen
                              hls.destroy();
                              delete hlsInstancesRef.current[idx];
                              setRenderingElements(prev => ({ ...prev, [idx]: true }));
                              startHlsPolling(idx, getSecureMediaUrl(hlsSrc));
                          });
                          return; // Kein weiterer Code — async oben übernimmt
                      }

                      hls.destroy();
                      delete hlsInstancesRef.current[idx];
                      video.src = mp4Src;
                      video.load();
                      if (idx === activeIndex) video.play().catch(() => {});
                  }
              });

              hlsInstancesRef.current[idx] = hls;
          }
      });
    });
  }, [lessons, activeIndex, applyAudioTrackPreference, isQRScannerOpen]);

  // Polling-Intervalle bei Unmount aufräumen
  useEffect(() => {
    return () => {
      Object.values(renderingPollRef.current).forEach(id => clearInterval(id));
      renderingPollRef.current = {};
    };
  }, []);

  // ── Proaktiver Rendering-Check ────────────────────────────────────────────
  // Wenn Lessons laden: sofort HEAD auf master.m3u8 für jedes Element.
  // Wenn 404 → Spinner sofort anzeigen + Polling starten,
  // ohne auf den HLS.js MANIFEST_LOAD_ERROR zu warten.
  // Verhindert das "leeres Video"-Fenster bei frisch erstellten Elementen.
  useEffect(() => {
    if (!lessons || lessons.length === 0) return;

    lessons.forEach((lesson, idx) => {
      if (!lesson?.url_element) return;
      // Nur prüfen wenn nicht bereits ein Polling läuft
      if (renderingPollRef.current[idx]) return;

      const rawUrl  = normalizeMediaUrl(lesson.url_element);
      const hlsSrc  = getSecureMediaUrl(rawUrl.replace('.mp4', '.m3u8'));
      const token   = localStorage.getItem('hejvi_access_token') || '';

      fetch(hlsSrc, {
        method: 'HEAD',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => {
          if (res.ok) return; // Stream bereit → nichts tun
          if (res.status === 404 && !renderingPollRef.current[idx]) {
            // ── MP4-Check: Altes Video (nur MP4, kein HLS) vs. wirklich noch rendernd ──
            // Wenn die MP4 existiert → altes Video ohne HLS. KEIN Spinner.
            // HLS.js löst später MANIFEST_LOAD_ERROR aus und fällt auf MP4 zurück.
            const mp4Url = getSecureMediaUrl(rawUrl.includes('.m3u8')
              ? rawUrl.replace('.m3u8', '.mp4')
              : rawUrl.replace('.m3u8', '.mp4') // rawUrl ist bereits .mp4 → rawUrl selbst
            );
            const mp4Token = token;
            fetch(mp4Url, {
              method: 'HEAD',
              headers: mp4Token ? { Authorization: `Bearer ${mp4Token}` } : {},
            }).then(mp4Res => {
              if (mp4Res.ok) {
                // MP4 vorhanden → altes Video, kein Spinner nötig
                console.log(`[HEJVI VIDEO] Proaktiver Check: kein HLS aber MP4 ok für idx=${idx} → kein Spinner (altes Video)`);
                return;
              }
              // MP4 auch nicht da → Video wird noch gerendert → Spinner + Polling
              console.log(`[HEJVI VIDEO] Proaktiver Check: master.m3u8 fehlt für idx=${idx} → Spinner`);
              setRenderingElements((prev) => ({ ...prev, [idx]: true }));
              startHlsPolling(idx, hlsSrc);
            }).catch(() => {
              // Netzwerkfehler beim MP4-Check → im Zweifel Spinner zeigen
              setRenderingElements((prev) => ({ ...prev, [idx]: true }));
              startHlsPolling(idx, hlsSrc);
            });
          } // if res.status === 404
        })
        .catch(() => { /* Netzwerkfehler beim initialen Check → ignorieren */ });
    });
  }, [lessons]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GlobalLayout
      title={collectionData?.name || (t('lessons') || 'Lessons')}
      showBackButton
      onBackClick={handleCustomBack}
      backTo={backCourseUrl}
      showNavbar={!isSimFullscreen}
    >
      <div
        ref={containerRef}
        className={`snap-container${isSimFullscreen ? ' sim-fullscreen' : ''}`}
        style={{
          height: isSimFullscreen ? viewportHeight : 'calc(100vh - 80px)',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'auto',   // 'smooth' + useScrollSnap erzeugt Bounce, der activeIndex kurz zurücksetzt
          overscrollBehavior: 'none',
          maxHeight: isSimFullscreen ? viewportHeight : 'calc(100vh - 80px)',
          width: '100%',
          margin: 0,
          padding: 0,
          position: 'relative'
        }}
        onTouchStart={(e) => {
          try {
            touchStartX.current = e.changedTouches[0].clientX;
            touchStartY.current = e.changedTouches[0].clientY;
          } catch (_) {
            touchStartX.current = null;
            touchStartY.current = null;
          }
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null || touchStartY.current == null) return;

          const endX = e.changedTouches[0].clientX;
          const endY = e.changedTouches[0].clientY;
          const deltaX = endX - touchStartX.current;
          const deltaY = endY - touchStartY.current;
          const threshold = 60;

          if (Math.abs(deltaX) > Math.abs(deltaY) * 2 && Math.abs(deltaX) > threshold) {
            if (deltaX < 0) {
              handleCustomBack();
            }
          }

          touchStartX.current = null;
          touchStartY.current = null;
        }}
        onScroll={(e) => {
          const container = e.currentTarget;
          const sections = Array.from(container.querySelectorAll('[data-snap-section]'));
          const top = container.scrollTop;
          const h = container.clientHeight;
          const maxScroll = container.scrollHeight - h;

          // ── SOFORT alle Videos pausieren, die nicht sichtbar sind ──
          // Synchron vor dem React-State-Update, damit keine zwei Videos
          // gleichzeitig laufen können.
          const pauseAllExcept = (keepIdx) => {
            videoRefs.current.forEach((vid, i) => {
              if (i !== keepIdx && vid instanceof HTMLVideoElement && !vid.paused) {
                try { vid.pause(); } catch (_) {}
              }
            });
          };

          if (top >= maxScroll - 1) {
            container.scrollTop = maxScroll;
            const lastIdx = sections.length - 1;
            pauseAllExcept(lastIdx);
            setActiveIndex(lastIdx);
            setShowEndPopup(false);
            return;
          }

          let bestIdx = 0;
          let bestDist = Infinity;
          sections.forEach((sec, i) => {
            const dist = Math.abs(sec.offsetTop - top);
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = i;
            }
          });

          if (bestIdx !== activeIndex) {
            pauseAllExcept(bestIdx);
            setShowEndPopup(false);
          }

          setActiveIndex(bestIdx);
        }}
      >
        {lessons.map((lesson, idx) => {
          return (
            <section
              key={lesson?.hash_id || (lesson?.id != null ? `${lesson.id}-${idx}` : `idx-${idx}`)}
              data-snap-section
              style={{
                height: isSimFullscreen ? viewportHeight : '100vh',
                scrollSnapAlign: 'start',
                position: 'relative',
                width: '100%',
                margin: 0,
                padding: 0
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#000',
                  width: '100%',
                  height: '100%'
                }}
                onTouchEnd={(e) => {
                  // Double-tap skip: nur für Videos (kein Quiz/Challenge)
                  if (Number(lesson?.type) === 3) return;
                  if (e.changedTouches.length !== 1) return;
                  const touch = e.changedTouches[0];
                  const now = Date.now();
                  const timeSince = now - lastTapTime.current;
                  const xDiff = Math.abs(touch.clientX - lastTapX.current);
                  const yDiff = Math.abs(touch.clientY - lastTapY.current);
                  if (timeSince < 300 && xDiff < 60 && yDiff < 60) {
                    // Double-Tap erkannt
                    if (singleTapTimeout.current) {
                      clearTimeout(singleTapTimeout.current);
                      singleTapTimeout.current = null;
                    }
                    const rect = e.currentTarget.getBoundingClientRect();
                    if (touch.clientX > rect.left + rect.width / 2) {
                      onSkipForward(idx);
                      setShowSkipIndicator('forward');
                    } else {
                      onSkipBackward(idx);
                      setShowSkipIndicator('backward');
                    }
                    setTimeout(() => setShowSkipIndicator(null), 800);
                    lastTapTime.current = 0;
                    lastTapX.current = 0;
                    lastTapY.current = 0;
                  } else {
                    lastTapTime.current = now;
                    lastTapX.current = touch.clientX;
                    lastTapY.current = touch.clientY;
                  }
                }}
              >
                {Number(lesson?.type) === 3 ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 18,
                      padding: '24px',
                      background: '#2D1653'
                    }}
                  >
                    
                    
                    <div style={{ height: 0, width: '100%' }} />
                    {/* ÄNDERUNG: Prüfung auf url_thumbnail ENTFERNT, damit der Platzhalter bleibt */}
                    {([0,2].includes(getNormalizedChallengeType(lesson))) && (
                      <div
                        style={{
                          width: '80%',
                          maxWidth: 420,
                          aspectRatio: '3/4',
                          // Wenn Bild da ist -> URL, sonst -> 'none'
                          backgroundImage: lesson?.url_thumbnail ? `url(${getSecureMediaUrl(lesson.url_thumbnail)})` : 'none',
                          // Optional: Leichter Hintergrund, wenn kein Bild da ist (Platzhalter-Look)
                          backgroundColor: lesson?.url_thumbnail ? 'transparent' : 'rgba(255,255,255,0.05)',
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          borderRadius: 16,
                          overflow: 'hidden',
                          boxShadow: '0 20px 40px rgba(0,0,0,.35)',
                          position: 'relative'
                        }}
                      />
                    )}
                    <div style={{ width: '100%', maxWidth: 780 }}>
                      {isDebug && (() => {
                        const { url: successElUrl } = resolveElementMediaById(lesson?.element_correct_id);
                        const { url: failureElUrl } = resolveElementMediaById(lesson?.element_incorrect_id);
                        const successUrl = successElUrl || normalizeMediaUrl(lesson?.url_success || lesson?.success_url);
                        const failureUrl = failureElUrl || normalizeMediaUrl(lesson?.url_failure || lesson?.failure_url);
                        console.info('[HEJVI DEBUG] Challenge', {
                          name: lesson?.name,
                          id: lesson?.id,
                          challenge_type: lesson?.challenge_type,
                          correct_option: lesson?.correct_option,
                          fulltext: lesson?.fulltext,
                          element_correct_id: lesson?.element_correct_id,
                          element_incorrect_id: lesson?.element_incorrect_id,
                          resolved_success_url: successUrl,
                          resolved_failure_url: failureUrl
                        });
                        
                        
                        return (
                          <div style={{
                            background: 'rgba(17,24,39,0.72)',
                            color: '#fff',
                            padding: '8px 12px',
                            borderRadius: 10,
                            marginBottom: 8,
                            fontSize: 12
                          }}>
                            <div>debug: type={String(lesson?.challenge_type)}→{getNormalizedChallengeType(lesson)} opt={String(lesson?.correct_option)} iOS={isIOS}</div>
                            <div>ok: {successUrl ? 'success✔' : 'success✖'} / {failureUrl ? 'failure✔' : 'failure✖'}</div>
                          </div>
                        );
                      })()}
                      {/* --- START: TITEL & BESCHREIBUNG (Mehrsprachig) --- */}
                      <div style={{
                        padding: '4px 8px',
                        marginTop: 18,
                        textAlign: 'left'
                      }}>
                        {([0,1,2,3,4].includes(getNormalizedChallengeType(lesson))) && (
                          <div style={{textAlign: 'center', marginBottom: 20}}>
                              {/* 1. Titel (Frage) - Automatisch übersetzt */}
                              <div style={{ color: '#FFFFFF', fontSize: 22, fontWeight: 800, marginBottom: 8, lineHeight: 1.2 }}>
                                {(() => {
                                  // Nutzung der Helper Funktion
                                  const val = getLocalizedContent(lesson, 'name', currentLanguage);
                                  // Fallback auf leeren String um Abstürze zu verhindern
                                  const q = String(val || '').trim();
                                  // Fragezeichen Logik: Nur anhängen, wenn noch keins da ist
                                  return String(val || t('') || '').trim();
                                })()}
                              </div>

                              {/* 2. Beschreibung (NEU) - Automatisch übersetzt */}
                              {(() => {
                                  const desc = getLocalizedContent(lesson, 'description', currentLanguage);
                                  if (!desc) return null;
                                  return (
                                      <div style={{ color: '#D1D5DB', fontSize: 16, fontWeight: 400, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                                          {desc}
                                      </div>
                                  );
                              })()}
                          </div>
                        )}
                        {/* --- ENDE: TITEL & BESCHREIBUNG --- */}
                        {getNormalizedChallengeType(lesson) === 0 || getNormalizedChallengeType(lesson) === 1 ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                            <button
                              onClick={async (e) => {
                                try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
                                const correctAnswer = String(lesson?.correct_option ?? lesson?.correct_answer ?? lesson?.correct ?? '1').toLowerCase();
                                const isYesCorrect = correctAnswer === 'yes' || correctAnswer === 'true' || correctAnswer === '1';
                                console.log('[HEJVI DEBUG] Yes button clicked', {
                                  correctAnswer,
                                  isYesCorrect,
                                  lesson: lesson?.title || lesson?.name,
                                  correctHash: lesson?.response_correct_hash,
                                  incorrectHash: lesson?.response_incorrect_hash,
                                  correctElementId: lesson?.element_correct_id,
                                  incorrectElementId: lesson?.element_incorrect_id
                                });

                                const correctHash = lesson?.response_correct_hash;
                                const incorrectHash = lesson?.response_incorrect_hash;
                                const correctElementId = lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id;
                                const incorrectElementId = lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id;

                                console.log('[HEJVI DEBUG] Challenge hash values:', {
                                  correctHash,
                                  incorrectHash,
                                  lessonName: lesson?.name,
                                  lessonId: lesson?.id,
                                  lessonHashId: lesson?.hash_id
                                });

                                try {
                                  await setChallenge({
                                    challenge_id: Number(lesson?.id || idx),
                                    answer_id: 'yes',
                                    correct_answer_id: (correctAnswer === 'yes' || correctAnswer === 'true' || correctAnswer === '1') ? 'yes' : 'no',
                                    help_used: Number(hintHighlights[idx] ? 1 : 0)
                                  });
                                  if (isYesCorrect) await awardQuizXP(lesson);
                                } catch (_) {}

                                await playChallengeVideo({
                                  isYesCorrect,
                                  idx,
                                  lesson,
                                  correctElementId: lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id,
                                  incorrectElementId: lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id,
                                  correctHash: lesson?.response_correct_hash,
                                  incorrectHash: lesson?.response_incorrect_hash
                                });
                              }}
                              style={{
                                flex: 1,
                                maxWidth: 240,
                                background: hintHighlights[idx] === 'yes' ? '#00ff00' : '#00ff00',
                                color: '#000000',
                                border: 0,
                                borderRadius: 12,
                                padding: '10px 12px',
                                fontWeight: 700,
                                fontSize: 18,
                                boxShadow: hintHighlights[idx] === 'yes' ? '0 0 0 3px rgba(239,68,68,0.45), 0 6px 12px rgba(231,74,59,0.28)' : '0 6px 12px rgba(231,74,59,0.28)',
                                transform: hintHighlights[idx] === 'yes' ? 'scale(1.03)' : 'scale(1)',
                                transition: 'all 160ms ease-out'
                              }}
                              aria-label="Yes"
                            >
                              ✓ {t('yes') || 'Yes'}
                            </button>
                            <button
                              onClick={async (e) => {
                                try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
                                const correctAnswer = String(lesson?.correct_option ?? lesson?.correct_answer ?? lesson?.correct ?? '1').toLowerCase();
                                const isNoCorrect = correctAnswer === 'no' || correctAnswer === 'false' || correctAnswer === '0';
                                console.log('[HEJVI DEBUG] No button clicked', {
                                  correctAnswer,
                                  isNoCorrect,
                                  lesson: lesson?.title || lesson?.name,
                                  correctHash: lesson?.response_correct_hash,
                                  incorrectHash: lesson?.response_incorrect_hash,
                                  correctElementId: lesson?.element_correct_id,
                                  incorrectElementId: lesson?.element_incorrect_id
                                });

                                const correctHash = lesson?.response_correct_hash;
                                const incorrectHash = lesson?.response_incorrect_hash;
                                const correctElementId = lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id;
                                const incorrectElementId = lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id;

                                try {
                                  await setChallenge({
                                    challenge_id: Number(lesson?.id || idx),
                                    answer_id: 'no',
                                    correct_answer_id: (correctAnswer === 'no' || correctAnswer === 'false' || correctAnswer === '0') ? 'no' : 'yes',
                                    help_used: Number(hintHighlights[idx] ? 1 : 0)
                                  });
                                  if (isNoCorrect) await awardQuizXP(lesson);
                                } catch (_) {}

                                await playChallengeVideo({
                                  isYesCorrect: isNoCorrect,
                                  idx,
                                  lesson,
                                  correctElementId: lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id,
                                  incorrectElementId: lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id,
                                  correctHash: lesson?.response_correct_hash,
                                  incorrectHash: lesson?.response_incorrect_hash
                                });
                              }}
                              style={{
                                flex: 1,
                                maxWidth: 240,
                                background: hintHighlights[idx] === 'no' ? '#ff0000' : '#ff0000',
                                color: '#000000',
                                border: 0,
                                borderRadius: 12,
                                padding: '10px 12px',
                                fontWeight: 700,
                                fontSize: 18,
                                boxShadow: hintHighlights[idx] === 'no' ? '0 0 0 5px rgba(255,255,255,1), 0 6px 12px rgba(255,255,255,0.9)' : '0 6px 12px rgba(54,178,74,0.28)',
                                transform: hintHighlights[idx] === 'no' ? 'scale(1.06)' : 'scale(1)',
                                transition: 'all 160ms ease-out'
                              }}
                              aria-label="No"
                            >
                              ✗ {t('no') || 'No'}
                            </button>
                          </div>
                        ) : getNormalizedChallengeType(lesson) === 2 ? (
                          // QR Scanner challenge design
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
                            <button
                              onClick={(e) => {
                                try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
                                qrPendingIndexRef.current = idx;
                                setIsQRScannerOpen(true);
                              }}
                              style={{
                                width: 92,
                                height: 92,
                                borderRadius: 46,
                                background: '#FF7A00',
                                border: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 12px 28px rgba(0,0,0,0.35)'
                              }}
                              aria-label="Scan QR"
                            >
                              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 7h4l2-2h6l2 2h4v12H3V7z"/>
                                <circle cx="12" cy="13" r="4"/>
                              </svg>
                            </button>
                          </div>
                        ) 
                        
                        /* ---------------------------------------------------------- */
                        /* --- TYPE 3: MULTIPLE CHOICE (DYNAMIC: LISTE ODER GRID) --- */
                        /* ---------------------------------------------------------- */
                        : getNormalizedChallengeType(lesson) === 3 ? (
                          <div style={{ width: '100%', maxWidth: 460, margin: '0 auto', padding: '0 10px' }}>
                            {(() => {
                              // 1. Einstellungen lesen
                              const n = Math.max(2, Math.min(6, Number(lesson?.number_of_options || 4)));
                              const isTextMode = Number(lesson?.use_option_names) === 1;
                              const isCorrectIndex = String(lesson?.correct_option || '1');

                              // 2. Grid-Layout Logik (Nur für Bild-Modus relevant)
                              const gridTemplate = n === 2 ? '1fr 1fr' : (n === 4 ? '1fr 1fr' : '1fr 1fr 1fr');

                              // 3. Optionen vorbereiten (Daten sammeln)
                              const options = Array.from({ length: n }).map((_, i) => {
                                  const idx = i + 1;
                                  return {
                                      idx: idx,
                                      // Bild URL holen (für Grid Modus)
                                      url: normalizeMediaUrl(lesson?.[`option${idx}_thumb`]),
                                      // Text Name holen (für Text Modus) - Lokalisiert!
                                      text: getLocalizedContent(lesson, `option${idx}_name`, currentLanguage)
                                  };
                              });
                              
                              return (
                                <div 
                                  style={{ 
                                    display: isTextMode ? 'flex' : 'grid', 
                                    flexDirection: isTextMode ? 'column' : undefined,
                                    gridTemplateColumns: isTextMode ? undefined : gridTemplate,
                                    gap: 16,
                                    // iOS-specific fixes
                                    WebkitTouchCallout: 'none',
                                    WebkitUserSelect: 'none',
                                    touchAction: 'manipulation'
                                  }}
                                >
                                  {options.map((opt) => (
                                    <button
                                      key={opt.idx}
                                      onClick={async (e) => {
                                        try { 
                                          e.stopPropagation(); 
                                          e.preventDefault(); 
                                          if (navigator.vibrate) navigator.vibrate(50);
                                        } catch (_) {}
                                        
                                        const chosen = String(opt.idx);
                                        const isCorrect = chosen === isCorrectIndex;
                                        
                                        try {
                                          await setChallenge({
                                            challenge_id: Number(lesson?.id || idx),
                                            answer_id: chosen,
                                            correct_answer_id: isCorrectIndex,
                                            help_used: Number(hintHighlights[idx] ? 1 : 0)
                                          });
                                          if (isCorrect) await awardQuizXP(lesson);
                                        } catch (error) {}
                                        
                                        await playChallengeVideo({
                                          isYesCorrect: isCorrect,
                                          idx,
                                          lesson,
                                          correctElementId: lesson?.element_correct_id,
                                          incorrectElementId: lesson?.element_incorrect_id,
                                          correctHash: lesson?.response_correct_hash,
                                          incorrectHash: lesson?.response_incorrect_hash
                                        });
                                      }}
                                      onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                                      onTouchEnd={(e) => { 
                                          setTimeout(() => {
                                            if (e.currentTarget) e.currentTarget.style.transform = String(hintHighlights[idx]) === String(opt.idx) ? 'scale(1.02)' : 'scale(1)';
                                          }, 100);
                                      }}
                                      style={{
                                        background: '#1F103B', // Dunkles Lila für Kontrast
                                        border: String(hintHighlights[idx]) === String(opt.idx) ? '2px solid #22c55e' : '1px solid rgba(255,255,255,0.15)',
                                        borderRadius: 12,
                                        padding: 0, 
                                        overflow: 'hidden',
                                        boxShadow: String(hintHighlights[idx]) === String(opt.idx)
                                          ? '0 0 0 4px rgba(34,197,94,0.25), 0 12px 28px rgba(34,197,94,0.35)'
                                          : '0 4px 12px rgba(0,0,0,0.2)',
                                        transform: String(hintHighlights[idx]) === String(opt.idx) ? 'scale(1.02)' : 'scale(1)',
                                        transition: 'all 160ms ease-out',
                                        cursor: 'pointer',
                                        WebkitAppearance: 'none',
                                        minHeight: 44,
                                        // Layout Switch Styles
                                        width: '100%'
                                      }}
                                    >
                                      {/* --- INHALT DER BUTTONS --- */}
                                      {isTextMode ? (
                                          // A) TEXT LISTE VIEW
                                          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left' }}>
                                              {/* Index Circle */}
                                              <div style={{
                                                  flexShrink: 0,
                                                  width: 32, height: 32,
                                                  borderRadius: '50%',
                                                  background: 'rgba(255,255,255,0.1)',
                                                  color: '#fff',
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                  fontSize: 14, fontWeight: '800'
                                              }}>
                                                  {opt.idx}
                                              </div>
                                              {/* Text Content */}
                                              <div style={{ color: '#fff', fontSize: '17px', fontWeight: '600', lineHeight: 1.3 }}>
                                                  {opt.text || `Option ${opt.idx}`}
                                              </div>
                                          </div>
                                      ) : (
                                          // B) BILDER GRID VIEW (Original Layout)
                                          <div 
                                            style={{ 
                                              width: '100%', 
                                              aspectRatio: isIOS ? undefined : '2/3',
                                              height: isIOS ? '180px' : undefined,
                                              background: '#0B0620',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              position: 'relative'
                                            }}
                                          >
                                            {opt.url ? (
                                              <img 
                                                src={opt.url} 
                                                alt={`Option ${opt.idx}`} 
                                                style={{ 
                                                  width: '100%', height: '100%', 
                                                  objectFit: 'contain', 
                                                  display: 'block',
                                                  WebkitUserDrag: 'none'
                                                }}
                                              />
                                            ) : (
                                              <div style={{ color: '#fff', fontSize: 24, fontWeight: 'bold' }}>{opt.idx}</div>
                                            )}
                                          </div>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              );
                            })()}
                            
                            {/* Frage / Fallback Text unter dem Grid (nur bei Grid nötig, bei Liste ist Text im Button) */}
                            
                          </div>
                        ) 
                        /* ---------------------------------------------------------- */
                        : getNormalizedChallengeType(lesson) === 4 ? (
                          // --- TYPE 4: MULTIPLE CHOICE (NUMMERIERTE LISTE) ---
                          <div style={{ width: '100%', maxWidth: 460, margin: '0 auto', padding: '0 10px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {(() => {
                              const n = Math.max(2, Math.min(6, Number(lesson?.number_of_options || 4)));
                              
                              const options = Array.from({ length: n }).map((_, i) => {
                                  const idx = i + 1;
                                  const text = getLocalizedContent(lesson, `option${idx}`, currentLanguage);
                                  return { idx, text };
                              });

                              const isCorrectIndex = String(lesson?.correct_option || '1');
                              
                              return options.map((opt, i) => (
                                <button
                                  key={opt.idx}
                                  onClick={async (e) => {
                                    const chosen = String(opt.idx);
                                    const isCorrect = chosen === isCorrectIndex;
                                    try { await setChallenge({ challenge_id: Number(lesson?.id || idx), answer_id: chosen, correct_answer_id: isCorrectIndex, help_used: Number(hintHighlights[idx] ? 1 : 0) }); if (isCorrect) await awardQuizXP(lesson); } catch (error) {}
                                    await playChallengeVideo({ isYesCorrect: isCorrect, idx, lesson, correctElementId: lesson?.element_correct_id, incorrectElementId: lesson?.element_incorrect_id, correctHash: lesson?.response_correct_hash, incorrectHash: lesson?.response_incorrect_hash });
                                  }}
                                  style={{
                                    width: '100%',
                                    background: '#1F103B',
                                    border: String(hintHighlights[idx]) === String(opt.idx) ? '2px solid #22c55e' : '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: 12,
                                    padding: '16px 20px',
                                    overflow: 'hidden',
                                    boxShadow: String(hintHighlights[idx]) === String(opt.idx) ? '0 0 0 4px rgba(34,197,94,0.25)' : '0 4px 12px rgba(0,0,0,0.2)',
                                    transform: String(hintHighlights[idx]) === String(opt.idx) ? 'scale(1.02)' : 'scale(1)',
                                    transition: 'all 160ms ease-out',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    gap: 16,
                                    textAlign: 'left'
                                  }}
                                >
                                  {/* Nummer-Badge */}
                                  <div style={{ 
                                      flexShrink: 0,
                                      width: 32, 
                                      height: 32, 
                                      borderRadius: '50%', 
                                      background: 'rgba(255,255,255,0.15)', 
                                      color: '#fff', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center', 
                                      fontSize: 16, 
                                      fontWeight: 'bold' 
                                  }}>
                                    {opt.idx}
                                  </div>

                                  {/* Text */}
                                  <div style={{ color: '#fff', fontSize: '18px', fontWeight: '500', lineHeight: 1.4 }}>
                                    {opt.text || `Option ${opt.idx}`}
                                  </div>
                                </button>
                              ));
                            })()}
                          </div> ): (
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder={t('challengeAnswer') || 'Type your answer'}
                              style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px', fontSize: 16 }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const val = String(e.currentTarget.value || '').trim().toLowerCase();
                                  const expected = String(lesson?.fulltext || lesson?.correct_option || '').trim().toLowerCase();
                                  const isCorrect = expected && val === expected;

                                  console.log('[HEJVI DEBUG] Text input submitted', {
                                    userInput: val,
                                    expected,
                                    isCorrect,
                                    lesson: lesson?.title || lesson?.name
                                  });

                                  const correctHash = lesson?.response_correct_hash;
                                  const incorrectHash = lesson?.response_incorrect_hash;
                                  const correctElementId = lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id;
                                  const incorrectElementId = lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id;

                                  playChallengeVideo({
                                    isYesCorrect: isCorrect,
                                    idx,
                                    lesson,
                                    correctElementId: lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id,
                                    incorrectElementId: lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id,
                                    correctHash: lesson?.response_correct_hash,
                                    incorrectHash: lesson?.response_incorrect_hash
                                  });
                                }
                              }}
                            />
                            <button
                              onClick={(e) => {
                                const input = e.currentTarget.previousSibling;
                                if (!(input && input.value != null)) return;
                                const val = String(input.value || '').trim().toLowerCase();
                                const expected = String(lesson?.fulltext || lesson?.correct_option || '').trim().toLowerCase();
                                const isCorrect = expected && val === expected;

                                console.log('[HEJVI DEBUG] Submit button clicked', {
                                  userInput: val,
                                  expected,
                                  isCorrect,
                                  lesson: lesson?.title || lesson?.name
                                });

                                const correctHash = lesson?.response_correct_hash;
                                const incorrectHash = lesson?.response_incorrect_hash;
                                const correctElementId = lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id;
                                const incorrectElementId = lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id;

                                playChallengeVideo({
                                  isYesCorrect: isCorrect,
                                  idx,
                                  lesson,
                                  correctElementId: lesson?.element_correct_id ?? lesson?.correct_element_id ?? lesson?.element_correct_hash_id ?? lesson?.correct_element_hash_id,
                                  incorrectElementId: lesson?.element_incorrect_id ?? lesson?.incorrect_element_id ?? lesson?.element_incorrect_hash_id ?? lesson?.incorrect_element_hash_id,
                                  correctHash: lesson?.response_correct_hash,
                                  incorrectHash: lesson?.response_incorrect_hash
                                });
                              }}
                              style={{ background: '#111827', color: '#fff', border: 0, borderRadius: 12, padding: '14px 18px', fontWeight: 800 }}
                            >
                              {t('submitAnswer') || 'Submit'}
                            </button>
                          </div>
                        )}
                        {/* --- HINWEIS BUTTON (NUR WENN hint_active = 1) --- */}
                        {Number(lesson?.hint_active) === 1 && (
                        <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div
                            onClick={() => {
                              const correctRaw = String(lesson?.correct_option ?? lesson?.correct_answer ?? lesson?.correct ?? '1').toLowerCase();
                              let newVal = null;
                              
                              // Logik für JA/NEIN (Type 0) vs. ANDERE (Type 3, 4 etc.)
                              if (getNormalizedChallengeType(lesson) === 0) {
                                const correctIsYes = correctRaw === 'yes' || correctRaw === 'true' || correctRaw === '1';
                                newVal = correctIsYes ? 'yes' : 'no';
                              } else {
                                // Für Multiple Choice (Grid & Liste) ist die korrekte Option z.B. "2"
                                newVal = String(lesson?.correct_option || '1');
                              }
                              
                              setHintHighlights((prev) => ({ ...prev, [idx]: newVal }));
                              
                              if (hintTimersRef.current[idx]) {
                                try { clearTimeout(hintTimersRef.current[idx]); } catch (_) {}
                              }
                              hintTimersRef.current[idx] = setTimeout(() => {
                                setHintHighlights((prev) => ({ ...prev, [idx]: null }));
                                hintTimersRef.current[idx] = null;
                              }, 1600);
                            }}
                            style={{
                              background: '#FFFFFF',
                              borderRadius: 14,
                              padding: '18px 22px',
                              width: '100%',
                              maxWidth: 420,
                              boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 10,
                              color: '#111827',
                              fontWeight: 800,
                              fontSize: 16,
                              cursor: 'pointer',
                              transform: hintHighlights[idx] ? 'scale(1.02)' : 'scale(1)',
                              transition: 'all 160ms ease-out'
                            }}
                          >
                            <span role="img" aria-label="hint">💡</span>
                            {(() => {
                              const raw = String(t('needHint') || 'Hinweis zeigen?').trim();
                              const cap = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Hinweis zeigen??';
                              return cap;
                            })()}
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const playing = challengeState[idx]?.playing;
                      const targetElementId = challengeState[idx]?.targetElementId;
                      const targetHash = challengeState[idx]?.targetHash;
                      const randomUrl = challengeState[idx]?.randomUrl;

                      /*console.log('[HEJVI DEBUG] Overlay check', {
                        idx,
                        playing,
                        targetElementId,
                        targetHash,
                        randomUrl,
                        responseVideos: responseVideos[targetHash]
                      });*/

                      let overlayUrl = '';
                      let isHls = false;
                      if (playing && targetHash) {
                        const responseVideo = responseVideos[targetHash];
                        //console.log('[HEJVI DEBUG] Checking responseVideo for targetHash:', targetHash, responseVideo);
                        if (responseVideo && !responseVideo.loading && responseVideo.url) {
                          overlayUrl = responseVideo.url;
                          isHls = /\.m3u8(\?|$)/i.test(overlayUrl);
                          //console.log('[HEJVI DEBUG] Using hash-based video URL:', overlayUrl);
                        } else {
                          console.log('[HEJVI DEBUG] Hash-based video not ready:', {
                            responseVideo,
                            hasResponseVideo: !!responseVideo,
                            isLoading: responseVideo?.loading,
                            hasUrl: !!responseVideo?.url,
                            url: responseVideo?.url
                          });
                        }
                      } else if (playing && targetElementId) {
                        const targetElement = lessons.find(l => Number(l.id) === Number(targetElementId) || l.hash_id === targetElementId);
                        if (targetElement) {
                          overlayUrl = normalizeMediaUrl(targetElement.url_element);
                          isHls = /\.m3u8(\?|$)/i.test(overlayUrl);
                          //console.log('[HEJVI DEBUG] Using element ID-based video URL:', overlayUrl);
                        } else {
                          console.log('[HEJVI DEBUG] Element not found for ID:', targetElementId);
                        }
                      } else if (playing && randomUrl) {
                        overlayUrl = normalizeMediaUrl(randomUrl);
                        isHls = /\.m3u8(\?|$)/i.test(overlayUrl);
                        console.log('[HEJVI DEBUG] Using random failure video URL:', overlayUrl);
                      }

                      //console.log('[HEJVI DEBUG] Final overlay URL:', overlayUrl, 'isHLS:', isHls);

                      if (!playing && !(challengeState[idx]?.playing == null && challengeState[idx]?.status === 'failure')) return null;

                      return (
                        <div className="ef-overlay-fixed" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                          {(() => { try { document.body.style.overflow = 'hidden'; } catch (_) {} return null; })()}
                          <div className="ef-overlay-stage">
                            {!(getNormalizedChallengeType(lesson) === 2) && (
                            <button
                              onClick={onToggleMute}
                              aria-label={isMuted ? 'Unmute' : 'Mute'}
                              style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                zIndex: 30,
                                width: 40,
                                height: 40,
                                borderRadius: 20,
                                background: 'rgba(17,24,39,0.7)',
                                border: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {isMuted ? (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                                  <path d="M23 9L17 15" />
                                  <path d="M17 9L23 15" />
                                </svg>
                              ) : (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                                </svg>
                              )}
                            </button>
                            )}
                            {!overlayUrl && playing && targetHash && responseVideos[targetHash]?.error && !responseVideos[targetHash]?.loading && (
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#fff',
                                fontSize: '16px',
                                fontWeight: '600',
                                textAlign: 'center',
                                background: 'rgba(220,38,38,0.8)',
                                padding: '20px',
                                borderRadius: '10px',
                                maxWidth: '300px'
                              }}>
                                <div style={{ marginBottom: '10px' }}>⚠️ Response video not found</div>
                                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                                  The response video for this challenge doesn't exist yet.
                                </div>
                              </div>
                            )}
                            {overlayUrl && (() => {
                              //console.log('[HEJVI DEBUG] Rendering video element with URL:', overlayUrl);
                              return (
                              <video
                                className="ef-overlay-video"
                                src={overlayUrl}
                                
                                poster={getSecureMediaUrl(lesson?.url_thumbnail)}
                                playsInline
                                autoPlay
                                controls={false}
                                muted={isMuted}
                                crossOrigin="anonymous"
                                loop={false}
                                onLoadStart={() => console.log('[HEJVI DEBUG] Video load started:', overlayUrl)}
                                onLoadedData={() => console.log('[HEJVI DEBUG] Video data loaded:', overlayUrl)}
                                onCanPlay={(e) => {
                                    const video = e.currentTarget;
                                    // Wenn kein HLS.js aktiv ist (also Safari), hier Audio setzen
                                    // Wir nutzen hier einen unique key für das Overlay HLS
                                    const hlsKey = `overlay-${idx}`;
                                    if (!hlsInstancesRef.current[hlsKey]) {
                                        applyAudioTrackPreference(video, null);
                                    }
                                }}
                                onError={(e) => console.error('[HEJVI DEBUG] Video error:', e, overlayUrl)}
                                onPlay={(e) => {
                                  try { startProgressInterval(e.currentTarget, lesson, idx); } catch (_) {}
                                }}
                                onPause={() => {
                                  try { stopProgressInterval(); } catch (_) {}
                                }}
                                ref={(el) => {
                                  overlayVideoRef.current = el;
                                  if (el) {
                                    try {
                                      el.muted = isMuted;
                                      const prevSrc = overlaySourceRef.current[idx];
                                      // KORREKTUR 2: Konsistente Nutzung von overlayUrl
                                      const nextSrc = overlayUrl || '';
                                      if (prevSrc !== nextSrc) {
                                        overlayEndedOnceRef.current[idx] = false;
                                        overlaySourceRef.current[idx] = nextSrc;
                                      }
                                      try { el.loop = false; el.removeAttribute('loop'); } catch (_) {}
                                      
                                      // Init Logic ausgelagert in onLoadedMetadata, 
                                      // aber direkter Play-Versuch für iOS hier behalten:
                                      if (!isHls) {
                                        try { el.setAttribute('playsinline', ''); el.setAttribute('webkit-playsinline', ''); } catch (_) {}
                                        const promise = (() => { try { return el.play(); } catch (_) { return undefined; } })();
                                        if (promise && typeof promise.then === 'function') {
                                          promise.catch(() => {
                                            try { el.muted = true; } catch (_) {}
                                            try { el.play(); } catch (_) {}
                                          });
                                        }
                                      }
                                    } catch (_) {}
                                  }
                                }}
                              
                                onLoadedMetadata={(e) => {
                                  const video = e.currentTarget;
                                  const src = normalizeMediaUrl(lesson.url_element);
                                  
                                  // HLS.js Init passiert jetzt oben im useEffect (für Chrome).
                                  // Hier behandeln wir nur noch reine MP4s oder helfen Safari beim Autoplay.

                                  // 1. Fallback: Wenn es KEIN HLS ist (also mp4), Audio-Spur hier setzen
                                  if (!/\.m3u8(\?|$)/i.test(src)) {
                                      setTimeout(() => applyAudioTrackPreference(video, null), 100);
                                  }
                                  
                                  // 2. Autoplay-Hilfe (besonders für Safari wichtig)
                                  if (isAutoplayEnabled && idx === activeIndex) {
                                      // Kleiner Timeout hilft, Race-Conditions zu vermeiden
                                      setTimeout(() => {
                                          video.play().catch(() => {});
                                      }, 50);
                                  }
                                }}
                                
                                onTimeUpdate={(e) => {
                                  const v = e.currentTarget;
                                  setOverlayProgress((p) => ({ ...p, [idx]: { current: Number(v.currentTime || 0), duration: Number(v.duration || 0) } }));
                                  
                                  const dur = Number(v.duration || 0);
                                  const cur = Number(v.currentTime || 0);

                                  // Check ob Video fast zu Ende ist (0.2s Toleranz) UND ob wir das schon behandelt haben
                                  if (dur > 0 && dur - cur <= 0.2 && !overlayEndedOnceRef.current[idx]) {
                                    // 1. Sofort Flag setzen, damit es nur einmal passiert
                                    overlayEndedOnceRef.current[idx] = true;
                                    
                                    // 2. Video anhalten
                                    try { v.pause(); } catch (_) {}
                                    try { stopProgressInterval(); } catch (_) {}
                                    
                                    // 3. HLS aufräumen
                                    const hlsKey = `overlay-${idx}`;
                                    if (hlsInstancesRef.current[hlsKey]) {
                                      try { hlsInstancesRef.current[hlsKey].destroy(); } catch (_) {}
                                      delete hlsInstancesRef.current[hlsKey];
                                    }

                                    // 4. Status ändern (Overlay schließen)
                                    if (challengeState[idx]?.playing === 'success') {
                                      setChallengeState((p) => ({ ...p, [idx]: { status: 'success', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                      
                                      // Scrollen zum nächsten Video (verzögert)
                                      if (activeIndex === idx) {
                                          setTimeout(() => {
                                              onNextVideo();
                                          }, 100);
                                      }
                                    } else {
                                      // Failure Case: Overlay schließen, Failure-Buttons zeigen
                                      setChallengeState((p) => ({ ...p, [idx]: { status: 'failure', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                    }
                                  }
                                }}

                                onEnded={(e) => {
                                  try { document.body.style.overflow = ''; } catch (_) {}
                                  
                                  // Sicherheits-Check: Falls onTimeUpdate schon gefeuert hat, hier abbrechen
                                  if (overlayEndedOnceRef.current[idx]) return;
                                  overlayEndedOnceRef.current[idx] = true;
                                  
                                  const v = e?.currentTarget;
                                  if (v) try { v.pause(); } catch (_) {}

                                  const hlsKey = `overlay-${idx}`;
                                  if (hlsInstancesRef.current[hlsKey]) {
                                    try { hlsInstancesRef.current[hlsKey].destroy(); } catch (_) {}
                                    delete hlsInstancesRef.current[hlsKey];
                                  }
                                  try { stopProgressInterval(); } catch (_) {}

                                  if (challengeState[idx]?.playing === 'success') {
                                    setChallengeState((p) => ({ ...p, [idx]: { status: 'success', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                    
                                    if (activeIndex === idx) {
                                        setTimeout(() => {
                                            onNextVideo();
                                        }, 100);
                                    }
                                  } else {
                                    setChallengeState((p) => ({ ...p, [idx]: { status: 'failure', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                  }
                                }}
                              />
                            );
                            })()}
                            {overlayUrl && (() => {
                              const prog = overlayProgress[idx] || { current: 0, duration: 0 };
                              const pct = prog.duration > 0 ? Math.min(100, Math.floor((prog.current / prog.duration) * 100)) : 0;
                              return (
                                <div className="ef-overlay-hud">
                                  <div className="ef-overlay-progress" onClick={(e) => {
                                    const stage = e.currentTarget.closest('.ef-overlay-stage');
                                    const videoEl = stage ? stage.querySelector('.ef-overlay-video') : null;
                                    if (!videoEl || !videoEl.duration) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const clickX = e.clientX - rect.left;
                                    const percentage = clickX / rect.width;
                                    const newTime = percentage * videoEl.duration;
                                    try { videoEl.currentTime = Math.max(0, Math.min(videoEl.duration, newTime)); } catch (_) {}
                                  }}>
                                    <div className="ef-overlay-progress-fill" style={{ width: `${pct}%` }} />
                                  </div>
                                  <div className="ef-overlay-time">
                                    {formatTime(prog.current)} / {formatTime(prog.duration)}
                                  </div>
                                </div>
                              );
                            })()}
                            {challengeState[idx]?.playing == null && challengeState[idx]?.status === 'failure' && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  gap: 12,
                                  zIndex: 20
                                }}
                              >
                                <button
                                  onClick={() => {
                                    console.log('[HEJVI DEBUG] Replay clicked at quiz index', idx);
                                    setChallengeState((p) => ({ ...p, [idx]: { status: 'idle', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                    setReturnToChallenge({ targetIdx: idx });
                                    // Find previous non-challenge video (type !== 3). Fallback to first video.
                                    let targetPlayableIdx = 0;
                                    for (let i = idx - 1; i >= 0; i -= 1) {
                                      const ls = lessons[i];
                                      if (Number(ls?.type) !== 3 && ls?.url_element) {
                                        targetPlayableIdx = i;
                                        break;
                                      }
                                    }
                                    console.log('[HEJVI DEBUG] Replay navigating to previous video index:', targetPlayableIdx);
                                    if (lessons.length > 0 && lessons[targetPlayableIdx]?.url_element) {
                                      navigatingRef.current = true;
                                      setActiveIndex(targetPlayableIdx);
                                      setTimeout(() => {
                                        const section = videoRefs.current[targetPlayableIdx]?.closest('[data-snap-section]');
                                        if (section) {
                                          try {
                                            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            console.log('[HEJVI DEBUG] Scrolled to previous video');
                                          } catch (_) {}
                                        }
                                        const v = videoRefs.current[targetPlayableIdx];
                                        if (v) {
                                          try {
                                            // iOS-stable replay for previous video
                                            try { v.pause(); } catch (_) {}
                                            try { v.onended = null; } catch (_) {}
                                            try { v.currentTime = 0; } catch (_) {}
                                            try { if (v.load) v.load(); } catch (_) {}
                                            try { v.muted = isMuted; } catch (_) {}
                                            const pp = (() => { try { return v.play(); } catch (_) { return undefined; } })();
                                            if (pp && typeof pp.then === 'function') {
                                              pp.catch(() => {
                                                try { v.muted = true; } catch (_) {}
                                                try { v.play(); } catch (_) {}
                                              });
                                            }
                                            console.log('[HEJVI DEBUG] Playing previous video');
                                          } catch (_) {}
                                        }
                                        navigatingRef.current = false;
                                      }, 100);
                                    } else {
                                      console.log('[HEJVI DEBUG] First video not playable for replay');
                                      setReturnToChallenge(null);
                                      setChallengeState((p) => ({ ...p, [idx]: { status: 'idle', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                    }
                                  }}
                                  style={{ 
                                    background: '#fff', 
                                    border: 0, 
                                    borderRadius: 10, 
                                    padding: '12px 16px', 
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 4V10H7" />
                                    <path d="M23 20V14H17" />
                                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" />
                                  </svg>
                                  {t('replay') || 'Replay'}
                                </button>
                                <button
                                  onClick={() => {
                                    console.log('[HEJVI DEBUG] Continue button clicked - advancing to next video');
                                    setChallengeState((p) => ({ ...p, [idx]: { status: 'idle', playing: null, targetElementId: null, targetHash: null, randomUrl: null } }));
                                    if (activeIndex === idx) onNextVideo();
                                  }}
                                  style={{ 
                                    background: '#111827', 
                                    color: '#fff', 
                                    border: 0, 
                                    borderRadius: 10, 
                                    padding: '12px 16px', 
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 4V20L16 12L5 4Z" />
                                    <path d="M19 5V19" />
                                  </svg>
                                  {t('continue') || 'Continue'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                  {/* ── Rendering-Overlay: Video wird noch gerendert ────────── */}
                  {renderingElements[idx] && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 16,
                      background: lesson?.url_thumbnail
                        ? `linear-gradient(rgba(0,0,0,0.55),rgba(0,0,0,0.55)), url(${getSecureMediaUrl(lesson.url_thumbnail)}) center/cover no-repeat`
                        : '#111',
                    }}>
                      {/* Spinner */}
                      <div style={{
                        width: 52, height: 52,
                        border: '4px solid rgba(255,255,255,0.2)',
                        borderTop: '4px solid #FF6407',
                        borderRadius: '50%',
                        animation: 'hejvi-spin 1s linear infinite',
                      }} />
                      <style>{`@keyframes hejvi-spin { to { transform: rotate(360deg); } }`}</style>
                      <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, textAlign: 'center', padding: '0 24px' }}>
                        {t('videoRendering') || 'Video wird verarbeitet…'}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center', padding: '0 32px' }}>
                        {t('videoRenderingHint') || 'Das dauert meist weniger als eine Minute. Diese Seite aktualisiert sich automatisch.'}
                      </div>
                    </div>
                  )}
                  {/* ── Haupt-Video ─────────────────────────────────────────── */}
                  <video
                    id={lesson?.hash_id || (lesson?.id != null ? `video-${lesson.id}` : undefined)}
                    ref={(el) => (videoRefs.current[idx] = el)}
                    data-idx={idx}
                    src={getSecureMediaUrl(normalizeMediaUrl(lesson.url_element).replace('.mp4', '.m3u8'))}
                    poster={getSecureMediaUrl(lesson.url_thumbnail)}
                    playsInline
                    muted={isMuted}
                    controls={false}
                    preload="metadata"
                    crossOrigin="anonymous"
                    autoPlay={isAutoplayEnabled && idx === activeIndex}
                    style={{
                      width: '100%', objectFit: 'contain', position: 'absolute', inset: 0, display: 'block',
                      visibility: renderingElements[idx] ? 'hidden' : 'visible',
                    }}
                    
                    onClick={(e) => { e.target.paused ? e.target.play() : e.target.pause(); }}

                    // SAFARI AUDIO
                    onCanPlay={(e) => {
                        const video = e.currentTarget;
                        if (!hlsInstancesRef.current[idx]) {
                            applyAudioTrackPreference(video, null);
                        }
                    }}

                    // FALLBACK LOGIK (Repariert für Safari)
                    onError={(e) => {
                        const video = e.currentTarget;
                        if (video.dataset.fallbackApplied === 'true') return; // Loop-Guard
                        
                        let fallbackUrl = getSecureMediaUrl(normalizeMediaUrl(lesson.url_element));
                        if (fallbackUrl.includes('.m3u8')) {
                            // ?token= ist bereits in fallbackUrl — replace nur den Pfad-Teil
                            fallbackUrl = fallbackUrl.replace('.m3u8', '.mp4');
                        }
                        if (video.src && video.src.includes('.m3u8')) {
                            console.log("[Video] HLS failed (native), trying MP4 fallback:", fallbackUrl);
                            video.dataset.fallbackApplied = 'true';
                            video.src = fallbackUrl;
                            video.load();  // ← DAS war das fehlende Element!
                            if (isAutoplayEnabled && idx === activeIndex) {
                                video.play().catch(() => {});
                            }
                        }
                    }}

                    // SAFARI AUTOPLAY + LIVE-STREAM FALLBACK
                    onLoadedMetadata={(e) => {
                       const video = e.currentTarget;
                       // Nur relevant wenn kein hls.js läuft (also Safari native)
                       if (!hlsInstancesRef.current[idx]) {
                           // SAFARI BUG: Wenn .m3u8 kein #EXT-X-ENDLIST hat, wird duration=Infinity
                           // (Safari behandelt es fälschlicherweise als Live-Stream).
                           // In diesem Fall: Fallback auf .mp4
                           if (!isFinite(video.duration) && video.src && video.src.includes('.m3u8') && video.dataset.fallbackApplied !== 'true') {
                               console.log(`[HEJVI SAFARI] duration=Infinity detected (live-stream bug), switching to MP4`);
                               const mp4Url = getSecureMediaUrl(normalizeMediaUrl(lesson.url_element).replace('.m3u8', '.mp4'));
                               video.dataset.fallbackApplied = 'true';
                               video.src = mp4Url;
                               video.load();
                               if (isAutoplayEnabled && idx === activeIndex) {
                                   video.addEventListener('loadedmetadata', () => video.play().catch(() => {}), { once: true });
                               }
                               return;
                           }
                           if (isAutoplayEnabled && idx === activeIndex) {
                               video.play().catch(() => {});
                           }
                       }
                    }}
                  > 
                    {/* WICHTIG: Tag oben mit ">" öffnen, NICHT mit "/>" schließen! */}

                    {/* Untertitel werden als Custom-Overlay (ef-subtitle-overlay) gerendert,
                        nicht mehr als native <track>-Elemente — verhindert Safari-Box */}
                    
                  </video>
                  </>
                )}
                <div className="ef-overlay">
                  <button
                    className="ef-back"
                    onClick={handleCustomBack}
                    aria-label="Back"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M19 12H5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 19L5 12L12 5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    className="ef-mute"
                    onClick={onToggleMute}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                        <path d="M23 9L17 15" />
                        <path d="M17 9L23 15" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                      </svg>
                    )}
                  </button>

                  {/* ── CUSTOM SUBTITLE OVERLAY ─────────────────────────────────────
                      Ersetzt native <track>-Elemente vollständig. Kein Browser-
                      Rendering → kein Safari-Box-Problem. Volle CSS-Kontrolle.
                      Cues werden aus parseSrtToCues() geladen (state: subtitleTracks).
                  ─────────────────────────────────────────────────────────────────── */}
                  {showSubtitles && idx === activeIndex && subtitleTracks[idx] && (() => {
                    const targetLang = currentLanguage
                      ? String(currentLanguage).split('-')[0].toLowerCase()
                      : 'de';
                    const allTracks = subtitleTracks[idx];
                    const best = allTracks.find(t => t.lang === targetLang)
                               || allTracks.find(t => t.lang === 'en')
                               || allTracks[0];
                    if (!best?.cues) return null;
                    const ct = currentTime;
                    const activeCue = best.cues.find(c => ct >= c.start && ct < c.end);
                    if (!activeCue) return null;
                    return (
                      <div className="ef-subtitle-overlay">
                        <span className="ef-subtitle-text">
                          {activeCue.text.split('\n').map((line, i, arr) => (
                            <span key={i}>
                              {line}
                              {i < arr.length - 1 && <br />}
                            </span>
                          ))}
                        </span>
                      </div>
                    );
                  })()}
                  {subtitleTracks[idx] && subtitleTracks[idx].length > 0 && (
                      <button
                          style={{ 
                              position: 'absolute',
                              top: '76px',
                              right: '12px',
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              // RAHMEN: Grün wenn AN, Grau-Transparent wenn AUS
                              border: showSubtitles ? '2px solid #4ADE80' : '2px solid rgba(255,255,255,0.2)', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              background: 'rgba(0, 0, 0, 0.75)',
                              zIndex: 100,
                              cursor: 'pointer',
                              pointerEvents: 'auto',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.5)',
                              transition: 'all 0.2s ease' // Sanfter Übergang
                          }}
                          onClick={(e) => {
                              e.stopPropagation(); 
                              const newState = !showSubtitles;
                              setShowSubtitles(newState);
                              // Kein localStorage mehr, da bei jedem neuen Video Reset auf TRUE gewünscht ist
                          }}
                          aria-label="Toggle Subtitles"
                      >
                          <span style={{ 
                              // TEXT: Grün wenn AN, Grau wenn AUS
                              color: showSubtitles ? '#4ADE80' : '#9CA3AF', 
                              fontSize: '14px', 
                              fontWeight: '900',
                              lineHeight: 1
                          }}>
                              CC
                          </span>
                      </button>
                  )}

                  {/* ── DOKUMENT-OVERLAY-BUTTON ── */}
                  {(() => {
                    const doc = lesson?.source_document;
                    if (!doc) return null;
                    const rawDocUrl = doc.download_url || doc.file_url || null;
                    if (!rawDocUrl) return null;
                    const ext = (doc.mime_type || '').includes('pdf') ? 'PDF'
                      : (doc.mime_type || '').includes('presentation') || (doc.original_name || '').match(/\.pptx?$/i) ? 'PPT'
                      : (doc.mime_type || '').includes('word') || (doc.original_name || '').match(/\.docx?$/i) ? 'DOC'
                      : 'DOC';
                    const sizeStr = doc.file_size
                      ? (doc.file_size > 1048576 ? `${(doc.file_size / 1048576).toFixed(1)} MB` : `${Math.round(doc.file_size / 1024)} KB`)
                      : '';

                    const handleDocDownload = async (e) => {
                      e.stopPropagation();
                      // Immer frischen Token aus localStorage lesen
                      const token = localStorage.getItem('hejvi_access_token') || '';
                      const url = token
                        ? rawDocUrl + (rawDocUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token)
                        : rawDocUrl;
                      try {
                        const res = await fetch(url, {
                          headers: token ? { Authorization: `Bearer ${token}` } : {}
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = doc.original_name || `dokument.${ext.toLowerCase()}`;
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => { URL.revokeObjectURL(blobUrl); a.remove(); }, 1000);
                      } catch (err) {
                        console.error('[HejVi] Dokument-Download fehlgeschlagen:', err);
                      }
                    };

                    return (
                      <button
                        onClick={handleDocDownload}
                        aria-label={`Dokument herunterladen: ${doc.original_name || 'Dokument'}`}
                        style={{
                          position: 'absolute',
                          top: subtitleTracks[idx]?.length > 0 ? '126px' : '76px',
                          right: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: 'rgba(0,0,0,0.72)',
                          border: '1.5px solid rgba(255,255,255,0.22)',
                          borderRadius: '22px',
                          padding: '7px 12px 7px 9px',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          zIndex: 100,
                          pointerEvents: 'auto',
                          backdropFilter: 'blur(6px)',
                          WebkitBackdropFilter: 'blur(6px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.18s ease',
                          maxWidth: '160px',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,106,0,0.85)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.72)'; }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="12" y1="18" x2="12" y2="12"/>
                          <line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span style={{ opacity: 0.7, marginRight: '3px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>{ext}</span>
                          {sizeStr}
                        </span>
                      </button>
                    );
                  })()}

                  {/* ── INFO-BUTTON (Element-Detail) ── */}
                  {lesson?.hash_id && (() => {
                    const hasCC = subtitleTracks[idx]?.length > 0;
                    const hasDoc = !!lesson?.source_document;
                    // Stack (je ~50px Abstand): CC(70) → Doc(120) → Info
                    // ohne Chat-Button eine Ebene weniger als zuvor
                    let topOffset;
                    if (hasCC && hasDoc) topOffset = '176px';
                    else if (hasCC || hasDoc) topOffset = '126px';
                    else topOffset = '76px';
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/element/${lesson.hash_id}`);
                        }}
                        style={{
                          position: 'absolute',
                          top: topOffset,
                          right: '12px',
                          width: '42px',
                          height: '42px',
                          borderRadius: '50%',
                          border: '2px solid rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(0,0,0,0.75)',
                          zIndex: 100,
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          backdropFilter: 'blur(6px)',
                          WebkitBackdropFilter: 'blur(6px)',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.5)',
                          transition: 'background 0.18s ease, border-color 0.18s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,106,0,0.85)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(0,0,0,0.75)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                        }}
                        aria-label="Element-Details"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3"/>
                          <line x1="12" y1="12" x2="12" y2="16"/>
                        </svg>
                      </button>
                    );
                  })()}

                  {/* FragHejVi ist global via GlobalChatWidget — kein elementbezogener Chat mehr */}
                  {isVideoPaused && Number(lesson?.type) !== 3 && (
                    <div className="ef-pause-controls">
                      
                      <button className="ef-control-btn ef-play-btn" onClick={() => onTogglePlayPause(idx)} aria-label="Play">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5V19L19 12L8 5Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button className="ef-control-btn ef-replay-btn" onClick={() => onReplay(idx)} aria-label="Replay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M1 4V10H7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M23 20V14H17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {Number(lesson?.type) !== 3 && showEndPopup && idx === activeIndex && (
                    <div className="ef-end-popup">
                      <div className="ef-end-popup-content">
                        <div className="ef-end-timer">
                          {t('nextVideoIn')} {endPopupTimer}{t('seconds')}
                        </div>
                        <div className="ef-end-buttons">
                          <button className="ef-end-btn ef-end-replay-btn" onClick={() => onReplay(idx)} aria-label={t('replay')}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M1 4V10H7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M23 20V14H17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t('replay')}
                          </button>
                          <button
                            className="ef-end-btn ef-end-next-btn"
                            onClick={() => {
                              console.log('[HEJVI DEBUG] Next button clicked manually');
                              setShowEndPopup(false);
                              setEndPopupTimer(3);
                              onNextVideo();
                            }}
                            aria-label={t('next')}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M5 4V20L16 12L5 4Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M19 5V19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t('next')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {Number(lesson?.type) !== 3 && showSkipIndicator && idx === activeIndex && (
                    <div className={`ef-skip-indicator ef-skip-${showSkipIndicator}`}>
                      <div className="ef-skip-icon">
                        {showSkipIndicator === 'forward' ? (
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <path d="M13 5L22 12L13 19V5Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 5L11 12L2 19V5Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : (
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <path d="M11 19L2 12L11 5V19Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 19L13 12L22 5V19Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="ef-bottom">
                    {Number(lesson?.type) !== 3 && (
                      <>
                        {videoErrors[idx] && (
                          <div className="ef-error" style={{ color: '#ffb4b4', fontSize: 12 }}>
                            {t('error') || 'Error'}
                          </div>
                        )}
                        <div
                          className="ef-progress"
                          onClick={(e) => { e.stopPropagation(); onProgressBarClick(idx, e); }}
                          onMouseDown={(e) => { startScrub(idx, e.clientX, e.currentTarget, e); }}
                          onTouchStart={(e) => {
                            try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
                            startScrub(idx, e.touches[0].clientX, e.currentTarget, e);
                          }}
                          onMouseMove={(e) => onProgressHoverMove(idx, e.clientX, e.currentTarget)}
                          onMouseLeave={onProgressHoverLeave}
                          style={{ cursor: 'pointer', position: 'relative', zIndex: 20, pointerEvents: 'auto', touchAction: 'none' }}
                        >
                          <div
                            className="ef-progress-fill"
                            style={{
                              width:
                                duration > 0 && idx === activeIndex
                                  ? `${Math.min(100, Math.floor(((scrubState.active && scrubState.idx === idx) ? scrubState.pct * duration : currentTime) / duration * 100))}%`
                                  : '0%'
                            }}
                          />
                          {duration > 0 && idx === activeIndex && (
                            <div
                              className="ef-progress-thumb"
                              style={{
                                position: 'absolute',
                                top: -6,
                                height: 16,
                                width: 16,
                                borderRadius: 8,
                                background: '#fff',
                                boxShadow: '0 0 0 2px rgba(0,0,0,0.35)',
                                transform: 'translateX(-50%)',
                                left: `${Math.min(100, Math.max(0, ((scrubState.active && scrubState.idx === idx) ? scrubState.pct : (duration > 0 ? currentTime / duration : 0)) * 100))}%`,
                                pointerEvents: 'none'
                              }}
                            />
                          )}
                          {hoverState.active && hoverState.idx === idx && duration > 0 && (
                            <div
                              className="ef-progress-hover"
                              style={{
                                position: 'absolute',
                                bottom: 18,
                                transform: 'translateX(-50%)',
                                left: `${Math.min(100, Math.max(0, hoverState.pct * 100))}%`,
                                background: 'rgba(0,0,0,0.8)',
                                color: '#fff',
                                borderRadius: 6,
                                padding: '2px 6px',
                                fontSize: 12,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {formatTime(hoverState.pct * duration)}
                            </div>
                          )}
                        </div>
                        {idx === activeIndex && (
                          <div className="ef-time">
                            {formatTime(currentTime)} / {formatTime(duration)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
        
      
        {/* --- STYLE FÜR SICHTBARE & RESPONSIVE UNTERTITEL --- */}
        <style>{`
            video::cue {
                background-color: rgba(0, 0, 0, 0.75) !important;
                color: #ffffff !important;
                /* Schriftgröße responsive: Min 20px, Ideal 5vw, Max 36px */
                font-size: clamp(20px, 5vw, 36px) !important;
                line-height: 1.4 !important;
                font-family: "Helvetica Neue", Helvetica, Arial, sans-serif !important;
                font-weight: 600 !important;
                text-shadow: 1px 1px 2px #000000 !important;
                white-space: pre-wrap !important;
            }

            /* POSITIONIERUNG: GANZ UNTEN */
            video::-webkit-media-text-track-container {
                /* 0% = Ganz unten am Rand. -2% = Minimaler Abstand */
                transform: translateY(-2%) !important;
            }
            
            /* Für Firefox/andere Engine falls nötig */
            video::cue-region {
                transform: translateY(-2%) !important;
            }
        `}</style>

        
        {/* QR Scanner Modal */}
        <QRScanner
          isOpen={isQRScannerOpen}
          onClose={() => { setIsQRScannerOpen(false); 
            qrPendingIndexRef.current = null; }}
          onScan={handleQRScan}
        />

        {/* Unknown QR Code Modal */}
        {unknownQRModal.isOpen && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.75)',
              zIndex: 10000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setUnknownQRModal({ isOpen: false, scannedCode: null })}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#1f2937',
                borderRadius: '20px',
                padding: '32px 28px',
                maxWidth: '360px',
                width: '100%',
                boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center'
              }}
            >
              {/* Icon */}
              <div style={{ fontSize: '52px', marginBottom: '16px' }}>🔍</div>

              {/* Titel */}
              <h2 style={{
                color: '#fff',
                fontSize: '20px',
                fontWeight: '700',
                margin: '0 0 10px 0'
              }}>
                {t('unknownQRCodeTitle') || 'Unbekannter QR-Code'}
              </h2>

              {/* Gescannter Wert */}
              <p style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '12px',
                margin: '0 0 12px 0',
                wordBreak: 'break-all',
                fontFamily: 'monospace'
              }}>
                {unknownQRModal.scannedCode}
              </p>

              {/* Frage */}
              <p style={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '15px',
                margin: '0 0 28px 0',
                lineHeight: '1.5'
              }}>
                {t('unknownQRCodeQuestion') || 'Wie soll ich weiter verfahren?'}
              </p>

              {/* Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => {
                    const code = unknownQRModal.scannedCode;
                    setUnknownQRModal({ isOpen: false, scannedCode: null });
                    navigate('/create-course', { state: { prefilledQRCode: code } });
                  }}
                  style={{
                    background: '#FF6407',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50px',
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'opacity 0.15s'
                  }}
                  onMouseDown={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseUp={e => e.currentTarget.style.opacity = '1'}
                >
                  {t('unknownQRCodeCreateCollection') || 'Neue Collection anlegen'}
                </button>

                <button
                  onClick={() => setUnknownQRModal({ isOpen: false, scannedCode: null })}
                  style={{
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.6)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '50px',
                    padding: '13px 24px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel') || 'Abbrechen'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* --- GAMIFICATION OVERLAY --- */}
        {currentAchievement && (
          <div className="gamification-overlay" style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              zIndex: 9999,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              animation: 'fadeIn 0.3s ease-out'
          }}>
              {/* Konfetti Effekt (Simuliert durch CSS) */}
              <div className="confetti-container">
                  {[...Array(20)].map((_, i) => (
                      <div key={i} className="confetti" style={{
                          left: `${Math.random() * 100}%`,
                          animationDelay: `${Math.random() * 2}s`,
                          backgroundColor: ['#FF6407', '#FFD700', '#4CAF50', '#2196F3'][Math.floor(Math.random() * 4)]
                      }} />
                  ))}
              </div>

              <div className="gamification-card" style={{
                  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                  padding: '40px',
                  borderRadius: '24px',
                  textAlign: 'center',
                  border: '2px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                  maxWidth: '90%',
                  width: '340px',
                  animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
                  position: 'relative',
                  overflow: 'hidden'
              }}>
                  {/* Glanz-Effekt Hintergrund */}
                  <div style={{
                      position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 60%)',
                      pointerEvents: 'none'
                  }} />

                  {/* ICON (Unterscheidung Level vs Achievement) */}
                  <div style={{ fontSize: '80px', marginBottom: '20px', filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.6))' }}>
                      {currentAchievement.is_level_up ? '🚀' : '🏆'}
                  </div>

                  {/* HEADER */}
                  <h2 style={{ 
                      color: currentAchievement.is_level_up ? '#FFD700' : '#FF6407', // Gold für Level, Orange für Achiev.
                      fontSize: '24px', 
                      margin: '0 0 10px 0',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                  }}>
                      {currentAchievement.is_level_up 
                          ? (t('levelUp') || 'LEVEL UP!') 
                          : (t('achievementUnlocked') || 'ACHIEVEMENT!')}
                  </h2>

                  {/* NAME */}
                  {/* NAME (Automatisch übersetzt) */}
                  <h3 style={{ color: 'white', fontSize: '28px', margin: '0 0 30px 0', fontWeight: '800' }}>
                    {getLocalizedContent(currentAchievement, 'name', currentLanguage)}
                  </h3>
                  {/* BUTTON */}
                  <button 
                      onClick={() => setCurrentAchievement(null)}
                      style={{
                          background: 'white',
                          color: '#111827',
                          border: 'none',
                          padding: '14px 32px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          borderRadius: '50px',
                          cursor: 'pointer',
                          boxShadow: '0 4px 15px rgba(255, 255, 255, 0.3)',
                          transition: 'transform 0.1s',
                          width: '100%'
                      }}
                      onMouseDown={e => e.target.style.transform = 'scale(0.95)'}
                      onMouseUp={e => e.target.style.transform = 'scale(1)'}
                  >
                      {t('awesome') || 'Awesome!'}
                  </button>
              </div>

              {/* CSS Animationen direkt einfügen */}
              <style>{`
                  @keyframes popIn {
                      0% { transform: scale(0.5); opacity: 0; }
                      100% { transform: scale(1); opacity: 1; }
                  }
                  @keyframes fadeIn {
                      from { opacity: 0; }
                      to { opacity: 1; }
                  }
                  .confetti {
                      position: absolute;
                      width: 10px; height: 10px;
                      top: -10px;
                      border-radius: 50%;
                      animation: fall 3s linear infinite;
                  }
                  @keyframes fall {
                      to { transform: translateY(100vh) rotate(720deg); }
                  }
              `}</style>
          </div>
        )}
      </div>

      <GlobalChatWidget
        isOpen={globalChatOpen}
        onClose={() => setGlobalChatOpen(false)}
      />
    </GlobalLayout>
  );
};

export default ElementFeed;
